import type { PersistedIngestProgress, SpeechTranscript, BennieClient, SectionRegenProgress, Phase2CRefireResponse, RegenerateAllFailedResponse, AnalyticsSummary, WorkspacePipelineRow, AnalyticsToolboxUsage, WorkflowStatesResponse, ActivityEntry, SeverityTaxonomyResponse, IssueLogResponse, ExportIssueOrdinalsResponse, SymptomDto, UploadFilesResult, ChatRecoveryBaseline, ChatStreamObserver, ChatStopResult, ChatPlanTarget, ServiceTypeGrant } from '../BennieClient';
import type {
  ActiveOperations,
  CkFigureUrlMap,
  CkFigureUrlPair,
  CompletenessReport,
  EvidenceMapResponse,
  EvidenceMapping,
  ExportRequest,
  ExportResult,
  InheritedMdr,
  LatestExport,
  Plan,
  PlanItem,
  PlanItemProseFields,
  PlanProgressPhase as DomainPlanProgressPhase,
  ReferenceListResponse,
  Section,
  SectionVersion,
  TemplateStyleStatus,
  Workspace,
  WorkspaceFile,
  WorkspaceMessage,
} from '../../contracts/domain';
import type {
  AuditAdmissionDecision,
  AuditPhase,
  AuditRun,
  AuditRunTriggerAccepted,
  Checklist,
  ChecklistEdit,
  ChecklistItem,
  ChecklistRead,
  CompletenessScope,
  PlanKind,
  ReportPreflight,
  ReportVersionRoster,
} from '../../contracts/auditing';
import { isAuditAdmissionDecision, isReportPreflight, phaseTagOfPhase } from '../../contracts/auditing';
import { toCkFigureUrlMap } from '../../contracts/domain';
import { currentUiLanguage } from '../../../i18n';

/**
 * TD-1037 — the `?scope=` suffix for the two completeness endpoints.
 *
 * <p>🔴 <b>An omitted scope emits NO query string</b>, not `?scope=`. The workspace-wide request
 * stays byte-identical to the one that shipped, so validation and development send exactly the URL
 * they always sent and reach exactly the row they always reached.</p>
 */
function completenessScopeQuery(scope?: CompletenessScope): string {
  return scope ? `?scope=${encodeURIComponent(scope)}` : '';
}
import { backendBaseUrl } from '../../config';
import { transformKeys } from '../../utils/responseTransformer';
import { resolveApiError, resolveMessageCode } from '../../../lib/i18nResolve';

/**
 * R4.1-C (PF25): Custom Error subclass that preserves the structured backend error body.
 * Use this for any HTTP error that has actionable fields beyond `message`. Specifically:
 * - export endpoint returns `gap_sections: string[]`
 * - completeness endpoint returns `completeness_details: {...}`
 * - section regen endpoint returns `error_code: string`
 *
 * Callers can `instanceof BennieApiError` and access `body` for the structured payload.
 * For exception handlers that want a string, the standard `.message` field still works.
 */
export class BennieApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: Record<string, unknown>,
    public readonly endpoint: string
  ) {
    super(message);
    this.name = 'BennieApiError';
  }
}

/**
 * The user stopped a chat turn and the local stream was abandoned. Distinct from a transport drop so the
 * caller never enters stream-drop recovery (which would poll for, and adopt, a reply that persists
 * later) and never renders an error bubble for something the user asked for. The turn's real outcome
 * is whatever the backend persisted.
 */
export class ChatTurnAbortedError extends Error {
  constructor() {
    super('chat_turn_aborted');
    this.name = 'ChatTurnAbortedError';
  }
}

/**
 * M-6: distinct error for a wall-clock request timeout. Deliberately NOT a
 * DOMException('AbortError'): uploadSourceFiles' catch (~685-690) treats any
 * AbortError as a USER-CANCEL that is silently rethrown as "stop". A timeout is
 * a genuine failure that must join `failed[]`, so it surfaces as its own type
 * that the cancel branch does not match.
 */
export class TimeoutError extends Error {
  constructor(message = 'request_timeout') {
    super(message);
    this.name = 'TimeoutError';
  }
}

/**
 * Auditing rework rev 9 (PR 9 review) — the fetch-REJECTION shape, typed. `fetch()` rejects with a
 * `TypeError` only when the request never got a response (offline, DNS, CORS, connection reset);
 * `fetch` / `fetchRaw` re-throw exactly that rejection as this type, so a caller can tell "could not
 * reach the server" from any other `TypeError` (a bug in client code) without reading the message.
 * It extends `TypeError` and keeps the original message, so existing callers are unaffected.
 */
export class NetworkError extends TypeError {
  constructor(cause: TypeError) {
    super(cause.message);
    this.name = 'NetworkError';
  }
}

/** Re-types a fetch rejection; anything else (AbortError, TimeoutError) passes through. */
function asNetworkError(e: unknown): unknown {
  return e instanceof TypeError && !(e instanceof NetworkError) ? new NetworkError(e) : e;
}

/**
 * F-06: wall-clock cap on both hops of the report export (POST /export, then the
 * download of the rendered file). A DOCX render is a deterministic pandoc pass —
 * the Path A/Path B paths are documented at 5-15s and 2-5s — so five minutes is far
 * above any legitimate render while still being decisively finite. Before this cap
 * the export button had no bound at all: a hung roundtrip left "Drafting your
 * report…" spinning with no way out but a page reload.
 */
export const EXPORT_TIMEOUT_MS = 300_000;

/**
 * Wall-clock cap on the two workspace-CRUD roundtrips that drive a visible progress indicator:
 * `listWorkspaces` and `createWorkspace`.
 *
 * 🔴 ADDED 2026-08-22, AND IT CLOSES AN INESCAPABLE DIALOG. `fetch()` defaults `timeoutMs` to
 * undefined and SKIPS `withTimeout` entirely when it is, so neither of these had any bound at all.
 * On `CreateProjectDialog` the consequence was not a slow screen but a trap: a hung POST leaves
 * `busy` latched, which disables Cancel AND suppresses the dialog's own `onClose`, so the user
 * cannot leave without reloading the page. On `ModelProjects` the projects list simply spun for
 * ever. Both are the state `frontend-rules` names — "loading/progress indicators must have timeout
 * fallbacks — never spin forever".
 *
 * ⚠️ 30s, matching `views/model/modelApi.DEFAULT_TIMEOUT_MS` rather than inventing a third number:
 * these are small JSON reads and one small JSON write against the same API, and the registry client
 * beside them already settled what "too long" means for that shape. This is NOT the multipart or
 * export budget — those carry their own, far larger caps for genuinely long work.
 *
 * ➕ WIDENED 2026-08-28 TO THE WORKSPACE-TAKEOVER HYDRATION READ SET — `getWorkspace`,
 * `getWorkspaceMessages`, `getWorkflowState` and `listSections`. They had NO bound of any kind, and
 * three of them could not have carried one: they called the bare global `fetch` and never touched
 * this class's wrapper at all, so `withTimeout` was not merely unset but unreachable. `listSections`
 * did route through `this.fetch`, which SKIPS `withTimeout` entirely when `timeoutMs` is undefined —
 * so "the transport supports a bound" and "this read has one" were different claims, and only the
 * first was true. A hang on any of the four left `WorkspacePage` on a bare centred spinner that a
 * page reload was the only exit from.
 *
 * ⚠️ WHICH ASSUMPTION DID NOT CARRY, stated rather than papered over. The name says CRUD and the
 * paragraph above enumerates exactly two methods, because when it was written those were the two
 * callers. The hydration four are reads of workspace SUB-resources, not workspace-collection CRUD —
 * so the enumeration did not cover them while the REASONING did, to the word: small JSON reads,
 * same API, same "drives a visible progress indicator" trigger. The constant is therefore widened
 * rather than copied; a `WORKSPACE_HYDRATE_TIMEOUT_MS` sitting beside this one at the same value
 * would be a second number to keep in agreement and nothing else.
 */
export const WORKSPACE_CRUD_TIMEOUT_MS = 30_000;

/**
 * Auditing rework rev 9 — the server's wall clock for one background checklist generation,
 * `PlanGeneration:GenerationWallClockTimeoutSeconds` (default 1800 s). The client's in-progress watch
 * is bounded by it; the POST itself answers 202 on a CRUD bound.
 */
export const CHECKLIST_GENERATION_TIMEOUT_MS = 1_800_000;

/** `completeness_phases` → a map keyed by the phase wire value, whichever spelling arrived. */
function readCompletenessPhases(raw: Record<string, unknown> | undefined): Partial<Record<AuditPhase, string>> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const out: Partial<Record<AuditPhase, string>> = {};
  const pick = (a: string, b: string) => {
    const v = raw[a] ?? raw[b];
    return typeof v === 'string' && v.length > 0 ? v : undefined;
  };
  const de = pick('design_effective', 'designEffective');
  const oe = pick('operating_effective', 'operatingEffective');
  if (de) out.design_effective = de;
  if (oe) out.operating_effective = oe;
  return out;
}

/** Reads one checklist row off a raw plan body. A malformed body is a failure, never an empty list. */
function toChecklist(body: Record<string, unknown>, endpoint: string): Checklist {
  const planId = typeof body.id === 'string' ? body.id : (typeof body.plan_id === 'string' ? body.plan_id : null);
  const items = Array.isArray(body.items) ? body.items : null;
  if (!planId || typeof body.version !== 'number' || typeof body.status !== 'string' || !items) {
    throw new BennieApiError('checklist_malformed_response', 200, body, endpoint);
  }
  return {
    plan_id: planId,
    version: body.version,
    status: body.status,
    items: items.map((raw): ChecklistItem => {
      const it = raw as Record<string, unknown>;
      return {
        id: String(it.id ?? ''),
        title: typeof it.title === 'string' ? it.title : '',
        description: typeof it.description === 'string' ? it.description : '',
        method: typeof it.method === 'string' ? it.method : null,
        de_item_refs: Array.isArray(it.de_item_refs) ? it.de_item_refs.map(String) : undefined,
      };
    }),
  };
}

/**
 * ADR-043 / cycle-4 WI-11+WI-21: localize the user-facing message for a
 * BennieApiError. Reads `error_code + error_args` from the raw 4xx/5xx body
 * and resolves against the active UI locale's `errors.json`. Falls through
 * to the defensive English `message` field for unrecognized codes, then to
 * `HTTP {status}` as final fallback.
 *
 * The raw body is snake_case (NOT yet transformKeys-converted at error
 * branches); resolveApiError reads `error_args` directly.
 */
function localizeErrorBody(errorBody: Record<string, unknown>, status: number): string {
  const resolved = resolveApiError(errorBody as { error?: string; error_args?: Record<string, unknown>; message?: string; detail?: string });
  if (resolved) return resolved;
  const error = errorBody.error;
  if (typeof error === 'string') return error;
  return `HTTP ${status}`;
}

/**
 * Raw (POST-transformKeys) workspace shape. Every field here is camelCase, without
 * exception: `transformKeys` deep-camelCases EVERY key of EVERY response before any
 * mapper below runs, so a snake_case property simply does not exist on this object.
 *
 * A `[JsonPropertyName("output_language")]` decorator on the C# model names the field
 * ON THE WIRE — it says nothing about what survives the client-side transform, which
 * renames it to `outputLanguage` on arrival regardless. A previous version of this
 * comment claimed such fields "survive as snake_case"; acting on that produced the same
 * dead read three times (`output_language` in all three workspace mappers) plus
 * `latest_export_soft_warnings` in getWorkspace, each silently swallowing a persisted
 * value behind its default. Read camelCase here, always.
 *
 * Every field is optional; the mappers below supply defaults / `as Workspace` finalizes
 * the shape.
 */
interface RawWorkspace {
  id?: string;
  workspaceId?: string;
  name?: string;
  description?: string;
  reportType?: Workspace['reportType'];
  serviceSelection?: Workspace['serviceSelection'];
  status?: Workspace['status'];
  createdAt?: string;
  updatedAt?: string;
  gdAvailable?: boolean;
  outputLanguage?: 'en' | 'fr' | 'zh-CN';
  workflowState?: string;
  // ➕ G3 — the terminal-state instant, `section_finalizing_at` on the wire. Declared here as
  // well as on `Workspace` because THIS type is what the mapper reads FROM, and a field absent
  // from it cannot be mapped at all — the hand-written-allowlist drop the `modelId` comment
  // below records, one type earlier.
  sectionFinalizingAt?: string | null;
  // The persisted CK retrieval-scoping tags (`applied_to` → camelCased by transformKeys).
  // Absent on a generic / untagged workspace.
  appliedTo?: string[];
  // WI-25/WI-49 — the registry model this workspace hangs off (`model_id` → camelCased).
  // Absent on every workspace created before WI-44 makes it required.
  modelId?: string;
  // `latest_export_soft_warnings` → camelCased by transformKeys, element keys included.
  latestExportSoftWarnings?: Workspace['latestExportSoftWarnings'];
  // WI-26 — `capabilities.source_data_upload.{eligible,reason_code}` → deep-camelCased by
  // transformKeys, so it arrives here as `capabilities.sourceDataUpload.reasonCode`. Projected by
  // `GET /api/workspaces/{id}` only; ABSENT on every list response, by design.
  capabilities?: Workspace['capabilities'];
}

/**
 * Raw (post-transformKeys) workspace-file shape arriving on the wire. Carries
 * the canonical camelCase fields plus the legacy aliases the mapper falls back
 * to (`fileId`, `category`, `fileName`, `contentType`, `processingStatus`,
 * `createdAt`). Field unions mirror WorkspaceFile so the mapped object stays
 * assignable without per-field casts. The four `metadata`-promoted spec fields
 * are passed through untouched.
 */
interface RawWorkspaceFile {
  id?: string;
  fileId?: string;
  kind?: WorkspaceFile['kind'];
  category?: WorkspaceFile['kind'];
  filename?: string;
  fileName?: string;
  fileType?: string;
  contentType?: string;
  uploadedAt?: string;
  createdAt?: string;
  status?: WorkspaceFile['status'];
  processingStatus?: WorkspaceFile['status'];
  contentDescription?: string;
  dataType?: WorkspaceFile['dataType'];
  keyTopics?: string[];
  hasTables?: boolean;
  tableCount?: number;
  columnNames?: string[];
  rowCount?: number;
  retrievalHint?: WorkspaceFile['retrievalHint'];
  sourceSections?: string[];
  localPath?: string;
  blobUrl?: string;
  enrichmentStatus?: WorkspaceFile['enrichmentStatus'];
  // WI-14: hard-reject reason code (rejection_reason → rejectionReason via transformKeys). GET
  // /files returns the catalog doc unprojected, so it arrives whenever the BE stamped it.
  rejectionReason?: string;
  // TD-1128: `layout_analysis_status` → `layoutAnalysisStatus` via transformKeys (catalog doc unprojected).
  layoutAnalysisStatus?: string;
  enrichmentErrorStage?: string;
  cohort?: string | null;
  relationships?: WorkspaceFile['relationships'];
  graphStatus?: WorkspaceFile['graphStatus'];
  graphComputedAt?: string;
  emailSubject?: string;
  emailFrom?: string;
  emailTo?: string;
  emailDate?: string;
  parentFileId?: string;
  isEmailAttachment?: boolean;
  metadata?: Record<string, unknown>;
  // WI-11 (ADR-056): persisted MDR marker. update_file_catalog merges the upload
  // metadata flat onto the catalog doc (services/mcp/knowledge/server.py, grep
  // `existing.update(meta_copy)`), so origin='mdr' arrives as a TOP-LEVEL field
  // — NOT nested under metadata. It survives the model_development_report→
  // source_data promotion (FileEndpoints.cs, grep the block comment
  // `WI-09 (ADR-056): promote-in-place` — L3436-L3507 at 6a302d9eb).
  //
  // Both citations here were STALE and are corrected 2026-08-20 (WI-36):
  // `server.py:742` landed inside log_audit, and `FileEndpoints.cs:408-428`
  // landed in unrelated ingest-progress code — the promote block had moved
  // ~3000 lines. Each now carries a grep-able CONTENT PHRASE alongside the
  // line range, because the range is what expires: FileEndpoints.cs is 9,092
  // lines and under active edit, so a bare line number is stale on the next
  // commit while the phrase survives.
  //
  // ⚠️ The promotion is MULTIPART-ONLY. It lives in the
  // `MapPost("/files/source-data", … IFormFile file …)` handler; the SAS arm
  // (`/files/source-data/init` + `/files/source-data/finalize`) has NO MDR
  // promotion, so uploadOneViaSas below cannot promote an MDR and cannot even
  // report mdrPromotionFailed. See wiki/entities/cosmos-files.md "MDR
  // promote-in-place".
  //
  // Threaded into WorkspaceFile.metadata.origin below so the FE
  // top-pin + "auto-added reference" chip read it from persisted state.
  origin?: string;
  // Cycle 5 auditing: the catalog row's phase tag (`de` / `oe`), written by the upload's finalize
  // leg as a TOP-LEVEL field, like `origin` above. Promoted into metadata.audit_phase below, which
  // is where `phaseTagOf` (views/auditing/AuditingWorkspaceView.tsx) reads it.
  auditPhase?: string;
  coversSections?: unknown;
  source?: unknown;
}

/**
 * Raw (post-transformKeys) section shape arriving on the wire. `status` is a
 * bare string because the BE still emits legacy values (`completed`,
 * `approved`, `failed`, `exec_summary_blocked`) that the mapper translates into
 * the canonical SectionStatus union; unknown values are preserved as-is.
 */
interface RawSection {
  id?: string;
  sectionId?: string;
  sectionTitle?: string;
  fullTitle?: string;
  level?: number;
  status?: string;
  sectionType?: Section['sectionType'];
  latestVersion?: number;
  hasVersion?: boolean;
  versionStatus?: string;
  createdAt?: string;
  hasSubItems?: boolean;
  errorCode?: string;
  errorMessage?: string;
  errorStage?: string;
  retryCount?: number;
  failedAt?: string;
  lastErrorDetail?: Record<string, unknown>;
  figuresNotShownVersion?: number;
  blockingSections?: Section['blockingSections'];
}

/**
 * Raw (post-transformKeys) plan-item shape arriving on the wire. Carries both
 * the canonical camelCase fields and the legacy aliases the normalizer falls
 * back to (`templateSectionId`, `sectionTitle`, `expectedOutput`). Every field
 * is optional — the normalizer supplies defaults. `subItems` recurses on the
 * same raw shape.
 */
interface RawPlanItem {
  id?: string;
  templateSectionId?: string;
  title?: string;
  sectionTitle?: string;
  description?: string;
  requirements?: string[];
  type?: PlanItem['type'];
  category?: PlanItem['category'];
  subItems?: RawPlanItem[];
  method?: string;
  expectedOutput?: string;
}

/**
 * Raw (post-transformKeys) persisted-plan payload. Carried on the plan-stream
 * `complete` event and returned by GET /plan and GET /plan/{id}/versions/{v}.
 * A persisted plan always carries id / status / version / timestamps / items —
 * the version/versions mappers read those without a fallback — so they are
 * required. The template-style fields are optional (null when no template).
 */
interface RawPlanPayload {
  id: string;
  workspaceId?: string;
  status: Plan['status'];
  version: number;
  createdAt: string;
  updatedAt: string;
  items: RawPlanItem[];
  templateStyleStatus?: TemplateStyleStatus | null;
  templateStyleWarning?: string | null;
  templateStyleDictPresent?: boolean;
}

/**
 * GET /plan response envelope. The endpoint may return the bare plan, a
 * `{ plan: {...} }` wrapper, or a `{ status: 'no_plan' }` sentinel — so every
 * field is optional and `plan` recurses on the same envelope.
 */
interface RawPlanEnvelope extends Omit<Partial<RawPlanPayload>, 'status'> {
  status?: Plan['status'] | 'no_plan';
  plan?: RawPlanEnvelope;
}

/**
 * Terminal/progress phases the consumer-facing onProgress callback observes.
 *
 * B13 WI-71: this used to be a private hand-written union and is now an ALIAS of
 * the one contract in `contracts/domain.ts`. Two independent spellings of a
 * BE-owned vocabulary drifted in both directions without anything failing — see
 * the contract's own header for the exact members that were wrong.
 */
type PlanProgressPhase = DomainPlanProgressPhase;

/**
 * Consumer-facing plan-generation progress event (post-transformKeys). This is
 * the exact shape the BennieClient.generatePlanStream interface forwards to its
 * onProgress callback — no `heartbeat` phase (filtered before dispatch), no
 * `generation` stamp (internal-only). `plan` is the loose transport type the
 * completion mapper narrows to RawPlanPayload at the read site.
 */
interface PlanProgressEvent {
  phase: PlanProgressPhase;
  message?: string;
  messageCode?: string;
  messageArgs?: Record<string, unknown>;
  sections?: string[];
  total?: number;
  title?: string;
  index?: number;
  completed?: number;
  error?: string | null;
  plan?: Record<string, unknown>;
  step?: string;
  details?: { sections?: number; requirements?: number };
  progress?: number;
  // B13 WI-72: Stage-1 keepalive only. Elapsed vs declared budget — a different
  // claim from `progress`, rendered as one, never combined into a percentage.
  elapsedSeconds?: number;
  budgetSeconds?: number;
  enrichmentStatus?: 'partial' | 'complete';
  enrichedCount?: number;
  enrichedSections?: string[];
  errorClass?: string;
  retryRecommended?: boolean;
  suggestedAction?: string;
}

/**
 * Internal parse-time plan-stream event. Adds the `heartbeat` keepalive phase
 * and the monotonic `generation` stamp the BE attaches for stale-event
 * discard, plus an index signature so the whole event can be forwarded as the
 * BennieApiError structured body on a terminal failure.
 */
interface PlanStreamEvent extends Omit<PlanProgressEvent, 'phase'> {
  phase: PlanProgressPhase | 'heartbeat';
  generation?: number;
}

/**
 * Section-regeneration SSE event (post-transformKeys). `type` discriminates the
 * phase; a `complete` event additionally carries the new section version
 * fields the caller maps into a SectionVersion. The monotonic `generation`
 * stamp drives stale-event discard.
 */
interface RegenStreamEvent {
  type?: 'heartbeat' | 'progress' | 'complete' | 'error';
  phase?: string;
  message?: string;
  messageCode?: string;
  messageArgs?: Record<string, unknown>;
  /**
   * ⚠️ PROSE, not a code — the backend's `error` on an `error` frame is
   * `srex.UserMessage` (a defensive English sentence). The machine code is
   * `errorCode`. This is the OPPOSITE of the pre-stream 4xx body, where `error`
   * IS the code, and reading the two the same way is the defect fixed 2026-08-23.
   */
  error?: string;
  /** BE `error_code` — the stable machine code. THIS is what discriminates. */
  errorCode?: string;
  category?: string;
  generation?: number;
  // complete-event payload
  version?: number;
  content?: string;
  status?: string;
  createdAt?: string;
  citations?: SectionVersion['citations'];
}

/**
 * HTTP adapter for PRD v3 backend API.
 * Provides full workspace-scoped operations for the three-loop architecture.
 *
 * T-12: All API responses are deep-transformed from snake_case to camelCase via
 * transformKeys() in fetch/fetchFormData. Eliminates all ad-hoc dual-path mappings.
 */
/** The issue-log read query: only the params that were supplied, so an unscoped call keeps
 *  its byte-identical URL. */
function issueLogQuery(runId?: string, reportVersionId?: string): string {
  const params: string[] = [];
  if (runId) params.push(`runId=${encodeURIComponent(runId)}`);
  if (reportVersionId) params.push(`reportVersionId=${encodeURIComponent(reportVersionId)}`);
  return params.length ? `?${params.join('&')}` : '';
}

export class HttpBennieClient implements BennieClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = backendBaseUrl || '';
  }

  private async fetch<T>(
    endpoint: string,
    options: RequestInit = {},
    // F-06: optional wall-clock cap, composed through the same `withTimeout` the
    // multipart path uses. Omitted (the default) leaves every existing caller's
    // behaviour byte-identical; a caller that drives a progress indicator supplies
    // one so a hung roundtrip surfaces as a TimeoutError instead of spinning forever
    // (frontend-rules: "Loading/progress indicators must have timeout fallbacks").
    timeoutMs?: number
  ): Promise<T> {
    const url = `${this.baseUrl}/api${endpoint}`;
    // C3-063: options.signal (when supplied) is forwarded into the underlying
    // fetch via the spread so AbortController.abort() from the caller propagates
    // down to the network roundtrip. Without this, an AbortSignal threaded by
    // the upload-cancel path would be ignored and the fetch would run to
    // completion — leaving the UI flag stuck in the "processing" state.
    const doFetch = (signal?: AbortSignal) => fetch(url, {
      ...options,
      ...(signal ? { signal } : {}),
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
    let response: Response;
    try {
      response = timeoutMs === undefined
        ? await doFetch()
        : await this.withTimeout(timeoutMs, options.signal ?? undefined, doFetch);
    } catch (e) {
      throw asNetworkError(e);
    }

    if (!response.ok) {
      // R4.1-C (PF25): Preserve full structured error body via BennieApiError so callers
      // can access `gap_sections`, `error_code`, and other structured fields. Previously,
      // we only extracted `error.message` and threw a plain Error, dropping the rest of
      // the body. This caused PF25 — FE Snackbar showed a generic message and the user
      // had no way to see which sections caused an export failure.
      const errorBody = await response.json().catch(() => ({ detail: response.statusText }));
      const message = localizeErrorBody(errorBody, response.status);
      throw new BennieApiError(message, response.status, errorBody, endpoint);
    }

    const text = await response.text();
    if (!text || text === 'null') {
      throw new Error(`Empty response from ${endpoint}`);
    }
    return transformKeys<T>(JSON.parse(text));
  }

  /**
   * The SAME roundtrip as {@link fetch}, minus `transformKeys`.
   *
   * <p>🔴 <b>It exists for bodies whose OBJECT KEYS ARE DATA.</b> `transformKeys` walks a parsed
   * body and rewrites every key from snake_case to camelCase. That is correct for a DTO, where
   * every key is a field name, and wrong for a map, where the keys are values the user supplied:
   * an auditing run's <c>results</c> is keyed by register-item id (<c>C-01</c>, <c>P-4.2</c>), and
   * a blanket transform rewrites those ids. The corruption is silent — the body still parses, the
   * screen still renders, and only the lookup fails.</p>
   *
   * <p>⛔ Do NOT "fix" a caller of this by routing it back through {@link fetch}. If a body needs
   * camelCase fields AND has data-keyed maps, split the read, do not transform the map.</p>
   *
   * <p>Error handling is IDENTICAL to {@link fetch} on purpose — same `BennieApiError`, same
   * localized message, same preserved body — so a 409 lock reason reaches a caller of either
   * method in the same shape.</p>
   */
  private async fetchRaw<T>(
    endpoint: string,
    options: RequestInit = {},
    timeoutMs?: number
  ): Promise<T> {
    const url = `${this.baseUrl}/api${endpoint}`;
    const doFetch = (signal?: AbortSignal) => fetch(url, {
      ...options,
      ...(signal ? { signal } : {}),
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
    let response: Response;
    try {
      response = timeoutMs === undefined
        ? await doFetch()
        : await this.withTimeout(timeoutMs, options.signal ?? undefined, doFetch);
    } catch (e) {
      throw asNetworkError(e);
    }

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ detail: response.statusText }));
      const message = localizeErrorBody(errorBody, response.status);
      throw new BennieApiError(message, response.status, errorBody, endpoint);
    }

    const text = await response.text();
    if (!text || text === 'null') {
      throw new Error(`Empty response from ${endpoint}`);
    }
    return JSON.parse(text) as T;
  }

  private async fetchFormData<T>(
    endpoint: string,
    formData: FormData,
    signal?: AbortSignal,
    // M-6: multipart uploads default to a 600s wall-clock cap (large files).
    timeoutMs = 600_000
  ): Promise<T> {
    const url = `${this.baseUrl}/api${endpoint}`;
    // C3-063: signal forwards AbortController cancellation from the upload-cancel
    // path into the multipart roundtrip. AbortError surfaces as a fetch rejection
    // and is propagated to the caller as the user-cancel signal.
    // M-6: withTimeout composes the caller signal with a wall-clock deadline so a
    // hung upload can't spin forever; a timeout rejects with TimeoutError (a real
    // failure), distinct from the cancel-AbortError the caller swallows.
    const response = await this.withTimeout(timeoutMs, signal, (composed) =>
      fetch(url, {
        method: 'POST',
        body: formData,
        signal: composed,
      })
    );

    if (!response.ok) {
      // R4.1-C (PF25): Same BennieApiError pattern as fetch() — preserve full structured body.
      const errorBody = await response.json().catch(() => ({ detail: response.statusText }));
      const message = localizeErrorBody(errorBody, response.status);
      throw new BennieApiError(message, response.status, errorBody, endpoint);
    }

    return transformKeys<T>(await response.json());
  }

  /** Parse raw fetch JSON and transform keys */
  private async parseJson<T>(response: Response): Promise<T> {
    return transformKeys<T>(await response.json());
  }

  /**
   * M-6: wrap a fetch-producing operation with a wall-clock timeout.
   *
   * Upload/delete fetches previously had no timeout — only the SSE readers cap
   * their wait via READ_TIMEOUT_MS — so a hung connection could spin forever.
   *
   * Implementation note: we deliberately use Promise.race + a manual
   * AbortController rather than AbortSignal.timeout()/AbortSignal.any(), because
   * (a) those are not in this project's ES2020 lib typings, and (b) an aborted
   * fetch always rejects with a DOMException('AbortError') regardless of the
   * abort reason — which uploadSourceFiles' catch treats as a USER-CANCEL and
   * silently swallows. Racing against a setTimeout that rejects with the
   * distinct TimeoutError guarantees a timeout surfaces as a genuine failure
   * (joining `failed[]`) and is never mistaken for a cancel. The internal
   * controller still aborts the in-flight fetch so the socket is torn down.
   *
   * The caller's user-cancel `signal` is composed in: aborting it aborts the
   * internal controller too, so cancel still propagates to the network and the
   * resulting AbortError bubbles unchanged (NOT converted to a TimeoutError).
   */
  private async withTimeout<T>(
    timeoutMs: number,
    callerSignal: AbortSignal | undefined,
    run: (signal: AbortSignal) => Promise<T>
  ): Promise<T> {
    const controller = new AbortController();
    const onCallerAbort = () => controller.abort();
    if (callerSignal) {
      if (callerSignal.aborted) controller.abort();
      else callerSignal.addEventListener('abort', onCallerAbort, { once: true });
    }

    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        controller.abort();
        reject(new TimeoutError());
      }, timeoutMs);
    });

    try {
      return await Promise.race([run(controller.signal), timeout]);
    } finally {
      if (timer) clearTimeout(timer);
      if (callerSignal) callerSignal.removeEventListener('abort', onCallerAbort);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Workspace CRUD
  // ─────────────────────────────────────────────────────────────────────────────

  async transcribeSpeech(audio: Blob, signal?: AbortSignal): Promise<SpeechTranscript> {
    // Bound the complete roundtrip, including response-body parsing after headers arrive.
    return this.withTimeout(20_000, signal, composed => this.fetch<SpeechTranscript>('/speech/transcribe', {
      method: 'POST', body: audio, signal: composed, credentials: 'include',
      headers: { 'Content-Type': 'audio/wav' },
    }));
  }

  async listWorkspaces(modelId?: string): Promise<Workspace[]> {
    // ➕ TD-1023 — the narrowing moves to the SERVER. `GET /api/workspaces?model_id=` has applied
    // this filter since `R58`; the model tabs were fetching every workspace the caller owned and
    // discarding the rest client-side, which is a plain over-fetch on a list with no page size.
    // ⚠️ An empty/absent `modelId` sends NO parameter rather than an empty one: the endpoint reads
    // `model_id_invalid` off a present-but-malformed value, and `""` would be exactly that.
    const query = modelId ? `?model_id=${encodeURIComponent(modelId)}` : '';
    const data = await this.fetch<{
      workspaces: RawWorkspace[];
      count: number;
    }>(`/workspaces${query}`, {}, WORKSPACE_CRUD_TIMEOUT_MS);

    return (data.workspaces || []).map((ws): Workspace => ({
      id: ws.id ?? '',
      name: ws.name ?? '',
      description: ws.description,
      reportType: ws.reportType ?? 'custom',
      serviceSelection: ws.serviceSelection,
      status: ws.status ?? 'active',
      createdAt: ws.createdAt ?? '',
      updatedAt: ws.updatedAt ?? '',
      gdAvailable: ws.gdAvailable ?? false,
      // fr-locale Phase 3a: the wire's `output_language` reaches here as `outputLanguage`
      // (transformKeys). 'en' is the genuine no-value default, never a substitute for a
      // persisted 'fr'/'zh-CN'.
      outputLanguage: ws.outputLanguage ?? 'en',
      // WI-02 / WI-03 / WI-05: current workflow_state for the projects-list pill
      // (BE enriches via DocStoreService.EnrichWithWorkflowStateAsync). transformKeys
      // in this.fetch already converted snake_case → camelCase, so read camelCase.
      workflowState: typeof ws.workflowState === 'string' ? ws.workflowState : '',
      // ➕ G3 — the TERMINAL-STATE INSTANT (`section_finalizing_at`, migration 0040), mapped on
      // BOTH read methods because TD-1015 is the record of what a one-sided mapping costs: the
      // field arrives on the wire, this hand-written allowlist drops it, and the consumer renders
      // an absence the database does not hold.
      //
      // ⛔ `null`, NOT `''`, AND NEVER `?? updatedAt`. Absent is a real answer here — the project
      // has not finalized, or it predates the stamp — and `''` erases that while a coalesce to
      // `updatedAt` replaces it with a plausible wrong date.
      sectionFinalizingAt:
        typeof ws.sectionFinalizingAt === 'string' ? ws.sectionFinalizingAt : null,
      // The workspace's dimension selection. Dropped here until now, which is why the
      // project card could not name the scope the user picked even in principle.
      appliedTo: ws.appliedTo,
      // 🔴 THE SAME DROP, ONE FIELD LATER, AND THE COMMENT ABOVE PREDICTED IT.
      // `model_id` reached this method on the wire (measured live: 4 of 7 rows populated) and was
      // discarded here, because this mapper is a HAND-WRITTEN ALLOWLIST and the field was not on
      // it. The model homepage's Projects section therefore rendered "Projects (0)" against a
      // database holding four — a maskover with no error anywhere in the chain.
      //
      // ⚠️ AND IT DEFEATED A CORRECTION I HAD ALREADY MADE. I measured that the C# record carries
      // `[JsonPropertyName("model_id")]` and that `transformKeys` has no allowlist, and concluded
      // "modelId already arrives in apps/web". Both measurements were TRUE and the conclusion was
      // FALSE: they cover hops 1 and 2 of a THREE-hop chain, and the drop is at hop 3. Two probes
      // agreeing because they share a frame is `verification-discipline.md` §2a exactly.
      modelId: ws.modelId,
    }));
  }

  async getWorkspace(workspaceId: string): Promise<Workspace | null> {
    const url = `${this.baseUrl}/api/workspaces/${workspaceId}`;
    // Bounded by WORKSPACE_CRUD_TIMEOUT_MS (2026-08-28). Wrapped with `withTimeout` rather than
    // re-routed through `this.fetch`, and that is deliberate: `this.fetch` throws BennieApiError on
    // every non-2xx, which would destroy the 404 → `null` contract three lines below that callers
    // depend on to distinguish "no such workspace" from "the read failed". Reuse the mechanism,
    // keep the contract — the same shape `exportReport` uses for its two bare-fetch hops.
    const response = await this.withTimeout(WORKSPACE_CRUD_TIMEOUT_MS, undefined, (signal) => fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      signal,
    }));
    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error(`Failed to fetch workspace: ${response.status}`);
    }
    const ws = await this.parseJson<RawWorkspace>(response);
    return {
      id: ws.id ?? ws.workspaceId,
      name: ws.name,
      description: ws.description,
      reportType: ws.reportType,
      serviceSelection: ws.serviceSelection,
      status: ws.status,
      createdAt: ws.createdAt,
      updatedAt: ws.updatedAt,
      gdAvailable: ws.gdAvailable ?? false,
      // fr-locale Phase 3a: post-transformKeys read — same default semantics as listWorkspaces.
      outputLanguage: ws.outputLanguage ?? 'en',
      // The workspace's dimension selection — same read as listWorkspaces, so the
      // workspace header resolves the same scope the project card does.
      appliedTo: ws.appliedTo,
      // TD-1015: same read as listWorkspaces (WI-02/WI-03/WI-05) — the BE now sets
      // `workflow_state` on this single-workspace GET too, from the same point read it
      // already pays for to compute `capabilities` below. '' is the genuine no-value
      // default (workflow_state doc absent), never a substitute for a persisted value.
      workflowState: typeof ws.workflowState === 'string' ? ws.workflowState : '',
      // ➕ G3 — the TERMINAL-STATE INSTANT (`section_finalizing_at`, migration 0040), mapped on
      // BOTH read methods because TD-1015 is the record of what a one-sided mapping costs: the
      // field arrives on the wire, this hand-written allowlist drops it, and the consumer renders
      // an absence the database does not hold.
      //
      // ⛔ `null`, NOT `''`, AND NEVER `?? updatedAt`. Absent is a real answer here — the project
      // has not finalized, or it predates the stamp — and `''` erases that while a coalesce to
      // `updatedAt` replaces it with a plausible wrong date.
      sectionFinalizingAt:
        typeof ws.sectionFinalizingAt === 'string' ? ws.sectionFinalizingAt : null,
      // 🔴 WI-44 — THE SAME HAND-WRITTEN-ALLOWLIST DROP AS `listWorkspaces` HAD, ONE METHOD OVER.
      // `listWorkspaces` was fixed and this one was not, so a project opened by id knew nothing
      // about the model it hangs off while the same project in the list did. Shipping a create
      // contract that makes `model_id` REQUIRED while the read side silently discards it is the
      // published-but-unwired shape wearing the other hat — so it lands in the same change.
      //
      // ⚠️ ABSENT IS A NORMAL STATE, NOT AN ERROR. `workspaces.model_id` (0046) is NULLABLE and
      // every workspace created before this WI carries NULL — 3 of 7 on DEV, counted 2026-08-22.
      // A consumer renders the absence. ⛔ No "first model" fallback, no sentinel.
      //
      // ⚠️ A SIBLING MAPPER ASYMMETRY FOUND IN THE SAME SWEEP AND DELIBERATELY NOT FIXED HERE,
      // because nothing in this WI's contract reads it and a drive-by would be unverified:
      // `latestExportSoftWarnings` is mapped by this method and NOT by `listWorkspaces`. Same
      // class of defect as this line was. Named, not silently inherited. (`workflowState` WAS
      // this same class of asymmetry and is fixed below — TD-1015.)
      modelId: ws.modelId,
      // Cycle-4 W8 WI-27 (2026-05-25): persist-then-render — surface the workspace doc's
      // `latest_export_soft_warnings` field, which arrives as `latestExportSoftWarnings`
      // with its element keys camelCased too (the transform is deep). Null when the BE
      // has not yet stamped (no export run on this workspace).
      latestExportSoftWarnings: Array.isArray(ws.latestExportSoftWarnings)
        ? ws.latestExportSoftWarnings
        : null,
      // WI-26 — THE PROJECTED CAPABILITY BLOCK, and this line is the third hop of the chain the
      // `modelId` comment above records being dropped at. The C# property name and the total
      // `transformKeys` transform are hops 1 and 2; a hand-written allowlist mapper is where the
      // value actually goes missing, so it is added HERE and not merely declared on the type.
      //
      // ⛔ NO DEFAULT AND NO COERCION. An absent block stays absent: `?? { sourceDataUpload: {
      // eligible: false } }` would fabricate a refusal the server never issued, and the panel
      // could not then tell "the server says no" from "no server answer arrived".
      capabilities: ws.capabilities,
    } as Workspace;
  }

  /**
   * ➕ 🔴 **`W-02` (2026-09-05) — THE DEPLOYMENT-ENTITLEMENT AXIS, REACHABLE FROM THE PRODUCT AT
   * LAST.** See `BennieClient.ServiceTypeGrant` for the defect: the entitlement axis shipped with an
   * admin API and a deferred admin UI, the follow-on was never built, and the product could CREATE
   * a shortfall (Add collaborator on the Access tab) and could not CLEAR it.
   *
   * <p><b>ON THIS TRANSPORT RATHER THAN `modelRegistryFetch`, AND THAT IS A DECISION.</b> The
   * alternative was a fourth base in `modelRegistryFetch.ts` beside `modelFetch` / `taxonomyFetch`.
   * Refused: that module scopes itself in terms to *"the model-registry family, NOT a repo-wide
   * one"*, and `/api/admin/*` is not that family. This client owns the `/api/admin/*` calls, so the admin surface stays in one place instead of
   * two, and these three inherit `localizeErrorBody` — which is what makes the server's two refusals
   * arrive as their own already-shipped sentences (`subject_id_not_object_id`,
   * `unsupported_service_type`) rather than as a generic failure.</p>
   *
   * <p>⚠️ Bounded by `WORKSPACE_CRUD_TIMEOUT_MS`, because they are small
   * JSON calls against the same API driving a visible indicator, and `frontend-rules` requires the
   * bound. A second constant at the same value would be one more thing to keep in agreement.</p>
   */
  async listServiceTypeGrants(): Promise<ServiceTypeGrant[]> {
    const body = await this.fetch<{ grants?: ServiceTypeGrant[] }>(
      '/admin/service-type-grants', {}, WORKSPACE_CRUD_TIMEOUT_MS,
    );
    // ⛔ `?? []` is a SHAPE default, not an error fallback: the route answers `{ grants: [] }` for an
    // empty store and THROWS through `this.fetch` on every non-2xx, so this coalesce can only ever
    // absorb a well-formed success whose array is absent. It cannot hide a 403 — that never reaches
    // here.
    return body.grants ?? [];
  }

  /**
   * ⛔ **THE RESPONSE BODY IS DISCARDED, DELIBERATELY.** Both mutations answer with the refetched
   * `{ grants }` list, and taking it would be rendering from a response payload —
   * `frontend-rules` Persist-Then-Render, and the identical decision `modelAccessApi.ts` records for
   * its own four mutations. The caller re-reads through `listServiceTypeGrants`.
   *
   * ⛔ **AND NOTHING IS PRE-VALIDATED HERE.** `subjectId` is passed through untouched so the
   * server's `subject_id_not_object_id` refusal is the one the user sees; re-implementing a GUID
   * check client-side would convert a server refusal into a silent client drop, and would put a
   * second spelling of the canonicalisation rule (any parseable GUID form → D-format) in the tree.
   */
  async grantServiceType(subjectId: string, subjectType: string, serviceType: string): Promise<void> {
    await this.fetch(
      '/admin/service-type-grants',
      {
        method: 'PUT',
        body: JSON.stringify({
          subject_id: subjectId, subject_type: subjectType, service_type: serviceType,
        }),
      },
      WORKSPACE_CRUD_TIMEOUT_MS,
    );
  }

  /** The revoke half. Query parameters, not a body — the route reads them that way. */
  async revokeServiceType(subjectId: string, subjectType: string, serviceType: string): Promise<void> {
    const q = new URLSearchParams({
      subject_id: subjectId, subject_type: subjectType, service_type: serviceType,
    });
    await this.fetch(
      `/admin/service-type-grants?${q.toString()}`,
      { method: 'DELETE' },
      WORKSPACE_CRUD_TIMEOUT_MS,
    );
  }

  /**
   * WI-44 — model-rooted create. `model_id` is required; the scope tuple is inherited server-side
   * and is absent from this type on purpose. See `BennieClient.createWorkspace` for why the
   * ABSENCE is the enforcement.
   */
  async createWorkspace(input: {
    model_id: string;
    name: string;
    description?: string;
    reportType: Workspace['reportType'];
    service_path?: string[];
    serviceSelection?: Record<string, unknown>;
    leaf_title?: string;
    model_path_titles?: string[];
    // Forwarded as `output_language` JSON field; server validates {en, fr, zh-CN} per ADR-041.
    output_language?: 'en' | 'fr' | 'zh-CN';
    // Cycle 3 Phase 4a: workspace-level finding-strictness + aggregation knobs.
    // Set at creation, immutable post-creation. Server validates the enums; null/missing → "balanced".
    strictness_level?: 'lenient' | 'balanced' | 'strict';
    aggregation_aggressiveness?: 'consolidated' | 'balanced' | 'granular';
  }): Promise<Workspace> {
    const data = await this.fetch<RawWorkspace>('/workspaces', {
      method: 'POST',
      body: JSON.stringify(input),
    }, WORKSPACE_CRUD_TIMEOUT_MS);

    return {
      id: data.id,
      name: data.name,
      description: data.description,
      reportType: data.reportType,
      serviceSelection: data.serviceSelection,
      status: data.status,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      // fr-locale Phase 3a: surface the create response's language so callers can confirm
      // what the server persisted (validation may have rejected the request's value).
      // Post-transformKeys, so camelCase — a snake_case read here would "confirm" 'en'
      // for every workspace, including the ones created as fr / zh-CN.
      outputLanguage: data.outputLanguage ?? 'en',
    } as Workspace;
  }

  async updateWorkspace(
    workspaceId: string,
    input: { name: string; description?: string; reportType?: Workspace['reportType']; serviceSelection?: Record<string, unknown> }
  ): Promise<Workspace> {
    return this.fetch<Workspace>(`/workspaces/${workspaceId}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  async archiveWorkspace(workspaceId: string): Promise<void> {
    await this.fetch(`/workspaces/${workspaceId}/archive`, { method: 'POST' });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Loop 1: Plan Generation
  // ─────────────────────────────────────────────────────────────────────────────

  async uploadTaskDescription(workspaceId: string, file: File): Promise<{ file: WorkspaceFile }> {
    const formData = new FormData();
    formData.append('file', file);

    // T-12: transformer converts file_id→fileId, file_name→fileName automatically
    const response = await this.fetchFormData<{
      fileId: string;
      fileName: string;
      blobUrl: string;
      category: string;
      status: string;
    }>(`/workspaces/${workspaceId}/files/task-description`, formData);

    return {
      file: {
        id: response.fileId,
        workspaceId,
        kind: 'task_description',
        filename: response.fileName,
        uploadedAt: new Date().toISOString(),
        status: 'uploaded' as WorkspaceFile['status'],
      },
    };
  }

  async uploadAuditScopeDocument(workspaceId: string, file: File): Promise<{ file: WorkspaceFile }> {
    const formData = new FormData();
    formData.append('file', file);

    // T-12: transformer converts file_id→fileId, file_name→fileName automatically
    const response = await this.fetchFormData<{
      fileId: string;
      fileName: string;
      blobUrl: string;
      category: string;
      status: string;
    }>(`/workspaces/${workspaceId}/files/audit-scope`, formData);

    return {
      file: {
        id: response.fileId,
        workspaceId,
        kind: 'audit_scope',
        filename: response.fileName,
        uploadedAt: new Date().toISOString(),
        status: 'uploaded' as WorkspaceFile['status'],
      },
    };
  }

  async uploadOutputTemplate(workspaceId: string, file: File): Promise<{ file: WorkspaceFile }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.fetchFormData<{
      fileId: string;
      fileName: string;
      blobUrl: string;
      category: string;
      status: string;
    }>(`/workspaces/${workspaceId}/files/output-template`, formData);

    return {
      file: {
        id: response.fileId,
        workspaceId,
        kind: 'output_template',
        filename: response.fileName,
        uploadedAt: new Date().toISOString(),
        status: 'uploaded' as WorkspaceFile['status'],
      },
    };
  }

  // ⚠️ 2026-09-05 — W-16 (wave W2f) REPLACED <s>`uploadModelDevelopmentReport`</s> WITH THIS READ.
  // The deleted method POSTed to `/files/model-development-report`, a route whose row had had NO
  // READER since WI-31 re-pointed plan generation at the model registry on 2026-08-25. See
  // `BennieClient.ts` for the full record; the route is gone, so a surviving client method would
  // have been a call into a 404.
  //
  // 🔴 THE THREE OUTCOMES ARE PASSED THROUGH UNTOUCHED. `transformKeys` deep-camelCases the wire
  // shape (`mdr_version_id` → `mdrVersionId`); the `outcome` VALUES are data, not keys, so they
  // arrive exactly as `PlanMdrResolver` classified them. ⛔ Do not normalise, default or collapse
  // them here — the client's job is to carry the resolver's answer, and any repair applied on the
  // way is a second copy of a rule the API owns.
  async getInheritedMdr(workspaceId: string): Promise<InheritedMdr> {
    return this.fetch<InheritedMdr>(`/workspaces/${workspaceId}/mdr`);
  }

  /**
   * ➕ B15 — the workspace's most recent persisted export.
   *
   * 🔴 **404 → `null` IS THE CONTRACT, AND IT IS NARROW.** The route answers a typed 404
   * `export_record_not_found` for "this workspace has never completed an export", which is an
   * ANSWER rather than a failure. ⛔ Only 404 collapses: every other status still throws, because
   * a 500 or a 403 rendered as "no export yet" would hide an unreachable API behind an honest
   * absence — the mask-over `core-principles.md` forbids. Same shape and same reason as
   * {@link getReferenceList}.
   *
   * `transformKeys` deep-camelCases the wire shape (`export_id` → `exportId`), so the two fields
   * arrive as {@link LatestExport} declares them. `modelId` may be absent — ruling `R9`'s
   * pre-`0054` workspace — and the caller renders that as no control at all.
   */
  async getLatestExport(workspaceId: string): Promise<LatestExport | null> {
    try {
      const body = await this.fetch<{ export: LatestExport }>(`/workspaces/${workspaceId}/exports/latest`);
      return body.export;
    } catch (err) {
      if (err instanceof BennieApiError && err.status === 404) return null;
      throw err;
    }
  }

  async getPlan(workspaceId: string, kind: Extract<PlanKind, 'report'> = 'report'): Promise<{ plan: Plan | null }> {
    // SF-041: Use raw fetch to gracefully handle empty/null responses and { plan: null, status: "no_plan" }
    // Auditing rework rev 9 §4: `?kind=` is ALWAYS sent, so the server can make it required (C1).
    const url = `${this.baseUrl}/api/workspaces/${workspaceId}/plan?kind=${kind}`;
    // Bounded by WORKSPACE_CRUD_TIMEOUT_MS (2026-08-28) — a hydration leg. `withTimeout` keeps the
    // SF-041 empty/null handling below exactly as it was; `this.fetch` would have thrown on it.
    const response = await this.withTimeout(WORKSPACE_CRUD_TIMEOUT_MS, undefined, (signal) => fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      signal,
    }));

    if (!response.ok) {
      if (response.status === 404) {
        return { plan: null };
      }
      // CR16 F-014: use BennieApiError so structured body (error/message/category) survives.
      const errorBody = await response.json().catch(() => ({ detail: response.statusText }));
      const message = localizeErrorBody(errorBody, response.status);
      throw new BennieApiError(message, response.status, errorBody, `/workspaces/${workspaceId}/plan`);
    }

    const text = await response.text();
    if (!text || text === 'null') {
      return { plan: null };
    }

    const data = transformKeys<RawPlanEnvelope>(JSON.parse(text));

    // SF-041: Handle { plan: null, status: "no_plan" } format gracefully
    if (!data || data.status === 'no_plan' || (!data.items && !data.plan)) {
      return { plan: null };
    }

    // Handle wrapped { plan: { ... } } format
    const planData = data.plan || data;

    if (!planData || !planData.items) {
      return { plan: null };
    }

    return {
      plan: {
        id: planData.id,
        workspaceId: planData.workspaceId,
        status: planData.status,
        version: planData.version,
        createdAt: planData.createdAt,
        updatedAt: planData.updatedAt,
        items: this.normalizePlanItems(planData.items),
        templateStyleStatus: planData.templateStyleStatus ?? null,
        templateStyleWarning: planData.templateStyleWarning ?? null,
        templateStyleDictPresent: Boolean(planData.templateStyleDictPresent),
      } as Plan,
    };
  }

  // ── Auditing rework rev 9 — the DE / OE checklists ───────────────────────────────────────────
  //
  // 🔴 READ RAW, like the run reads below: an OE item's `de_item_refs` and every item id are DATA,
  // and the `Checklist` contract is spelled as the server spells it.

  async getChecklist(workspaceId: string, kind: Exclude<PlanKind, 'report'>): Promise<ChecklistRead> {
    const endpoint = `/workspaces/${workspaceId}/plan?kind=${kind}`;
    const body = await this.fetchRaw<Record<string, unknown>>(endpoint, {}, WORKSPACE_CRUD_TIMEOUT_MS);
    // `{ plan: null, status: "no_plan", generation_in_progress }` is the server's "none yet"; the
    // flag is the persisted generation lease. A missing flag is a malformed body, not "idle".
    if (body.status === 'no_plan' && body.plan == null) {
      if (typeof body.generation_in_progress !== 'boolean') {
        throw new BennieApiError('checklist_malformed_response', 200, body, endpoint);
      }
      const failed = body.generation_failed;
      if (failed != null && typeof failed !== 'string') {
        throw new BennieApiError('checklist_malformed_response', 200, body, endpoint);
      }
      return { checklist: null, generationInProgress: body.generation_in_progress, generationFailed: failed ?? null };
    }
    return { checklist: toChecklist(body, endpoint), generationInProgress: false, generationFailed: null };
  }

  async getChecklistVersion(workspaceId: string, planId: string, version: number): Promise<Checklist> {
    const endpoint = `/workspaces/${workspaceId}/plan/${planId}/versions/${version}`;
    const body = await this.fetchRaw<Record<string, unknown>>(endpoint, {}, WORKSPACE_CRUD_TIMEOUT_MS);
    return toChecklist(body, endpoint);
  }

  async generateChecklist(workspaceId: string, phase: 'de' | 'oe'): Promise<void> {
    // D7: the route answers 202 once it holds the generation lease and runs the model call as
    // background work (a synchronous answer outlived the SWA front door's 45 s request limit). The
    // caller re-reads the checklist and watches the lease; the body is not used.
    await this.fetchRaw<unknown>(
      `/workspaces/${workspaceId}/plan/checklist/generate`,
      { method: 'POST', body: JSON.stringify({ phase }) },
      WORKSPACE_CRUD_TIMEOUT_MS,
    );
  }

  async editChecklistItems(workspaceId: string, planId: string, edit: ChecklistEdit): Promise<void> {
    await this.fetchRaw<unknown>(
      `/workspaces/${workspaceId}/plan/${planId}/checklist/items`,
      { method: 'PATCH', body: JSON.stringify(edit) },
      WORKSPACE_CRUD_TIMEOUT_MS,
    );
  }

  async approveChecklist(workspaceId: string, planId: string, version: number): Promise<void> {
    await this.fetchRaw<unknown>(
      `/workspaces/${workspaceId}/plan/${planId}/checklist/approve`,
      { method: 'POST', body: JSON.stringify({ version }) },
      WORKSPACE_CRUD_TIMEOUT_MS,
    );
  }

  async getReportPreflight(workspaceId: string): Promise<ReportPreflight> {
    const endpoint = `/workspaces/${workspaceId}/evidence-map?scope=report`;
    const body = await this.fetchRaw<{ preflight?: ReportPreflight }>(endpoint, {}, WORKSPACE_CRUD_TIMEOUT_MS);
    const pf = body?.preflight;
    if (!isReportPreflight(pf)) {
      throw new BennieApiError('report_preflight_malformed_response', 200, body as Record<string, unknown>, endpoint);
    }
    return pf;
  }

  async getAuditRunAdmission(workspaceId: string, phase: AuditPhase): Promise<AuditAdmissionDecision> {
    const endpoint = `/workspaces/${workspaceId}/evidence-map?scope=${phaseTagOfPhase(phase)}`;
    const body = await this.fetchRaw<{ admission?: AuditAdmissionDecision }>(endpoint, {}, WORKSPACE_CRUD_TIMEOUT_MS);
    if (!isAuditAdmissionDecision(body?.admission))
      throw new BennieApiError('audit_admission_malformed_response', 200, body as Record<string, unknown>, endpoint);
    return body.admission;
  }

  async getPlanVersion(workspaceId: string, planId: string, version: number): Promise<Plan | null> {
    const url = `${this.baseUrl}/api/workspaces/${workspaceId}/plan/${planId}/versions/${version}`;
    const response = await fetch(url);
    if (!response.ok) return null;
    const data = await this.parseJson<RawPlanPayload>(response);
    if (!data || !data.items) return null;
    return {
      id: data.id,
      workspaceId: data.workspaceId || workspaceId,
      status: data.status,
      version: data.version,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      items: this.normalizePlanItems(data.items),
      templateStyleStatus: data.templateStyleStatus ?? null,
      templateStyleWarning: data.templateStyleWarning ?? null,
      templateStyleDictPresent: Boolean(data.templateStyleDictPresent),
    };
  }

  /**
   * Stream plan generation via SSE. Calls onProgress for each event.
   * Returns the final plan once complete.
   */
  async generatePlanStream(
    workspaceId: string,
    // PlanProgressEvent mirrors the BennieClient.generatePlanStream interface's
    // onProgress shape exactly (no heartbeat/generation). The parse loop reads
    // the wider internal PlanStreamEvent and only dispatches non-heartbeat
    // events here, so the narrowed event stays assignable to this param.
    onProgress: (event: PlanProgressEvent) => void
  ): Promise<{ plan: Plan }> {
    const url = `${this.baseUrl}/api/workspaces/${workspaceId}/plan/generate-stream`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      // CR16 F-014: structured error body preserved via BennieApiError.
      const errorBody = await response.json().catch(() => ({ detail: response.statusText }));
      const message = localizeErrorBody(errorBody, response.status);
      throw new BennieApiError(message, response.status, errorBody, `/workspaces/${workspaceId}/plan/generate-stream`);
    }

    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let finalPlan: Plan | null = null;
    let buffer = '';
    const READ_TIMEOUT_MS = 300_000; // 5 min inactivity timeout — matches backend Stage 2 maximum

    // Batch 02 NEW-008: per-call generation tracker.
    // Declared as a local variable inside this function so each call to generatePlanStream starts fresh —
    // no cross-session state, no need for explicit reset on navigation/unmount. The session-boundary reset
    // requirement from the package is satisfied by this lexical scoping (each invocation = a new closure
    // = a new lastKnownGeneration starting at 0).
    let lastKnownGeneration = 0;

    for (;;) {
      const readPromise = reader.read();
      const timeoutPromise = new Promise<{ done: true; value: undefined }>((resolve) =>
        setTimeout(() => resolve({ done: true, value: undefined }), READ_TIMEOUT_MS)
      );
      const { done, value } = await Promise.race([readPromise, timeoutPromise]);
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;

        // 2026-08-23 (lane B-1b): the `try` here USED TO WRAP THE WHOLE EVENT BODY, and its
        // catch re-threw only when the caught message contained the literal
        // 'Plan generation failed'. That literal is the FALLBACK arm of the terminal-error
        // throw below (`event.message || 'Plan generation failed'`), so whenever the backend
        // DID word the refusal — the normal case, and the only case for both
        // `plan_already_exists` and `plan_gen_in_progress` — the guard was false, the catch
        // body was otherwise EMPTY, and the refusal was swallowed. Control then reached the
        // post-loop poller, which fetched the PRE-EXISTING plan and returned it as the result
        // of a generation the backend had just refused: a success reported over a refused
        // write. Three prohibitions at once — classification by string-scanning
        // (`backend-rules.md`), an empty catch body, and error-shaped data returned as success
        // (`core-principles.md`).
        //
        // The scope of the guard is the fix, not a better predicate: the try exists for
        // `JSON.parse`, so it wraps `JSON.parse` and nothing else. Everything downstream —
        // including the terminal `BennieApiError` — now propagates on its own identity, with
        // no prose read anywhere. Discriminating on type instead of text also removes the
        // localisation hazard: `event.message` is BE prose the backend may reword at will.
        let event: PlanStreamEvent;
        try {
          // T-12: Transform SSE event data keys too
          event = transformKeys<PlanStreamEvent>(JSON.parse(line.slice(6)));
        } catch (parseErr) {
          // A specific, known case handled deliberately (NOT a catch-all): one SSE frame did
          // not parse. Skipping it keeps the stream readable — a garbled keepalive must not
          // kill a live generation — and the run still cannot end in a false success, because
          // `finalPlan` is only ever set by a `complete` frame that DID parse. Logged so the
          // discard is visible rather than silent.
          console.warn('[generatePlanStream] discarding unparseable SSE frame:', parseErr, line);
          continue;
        }

        // Batch 02 NEW-008: stale-event discard.
        // The backend stamps every payload with a monotonic `generation` integer for this connection.
        // If we somehow receive an event with a generation lower than what we've already seen, the event
        // came from a previous (dropped/superseded) connection — discard it. Generation 0 means
        // "not stamped" and is treated as always-current (backward-compatible for any event without it).
        const eventGeneration = typeof event?.generation === 'number' ? event.generation : 0;
        if (eventGeneration > 0 && eventGeneration < lastKnownGeneration) {
          continue; // stale event from a prior connection — drop it
        }
        if (eventGeneration > lastKnownGeneration) {
          lastKnownGeneration = eventGeneration;
        }

        // Batch 02 NEW-006: heartbeat events keep the connection alive but carry no progress payload.
        // The backend emits these every 5s during enrichment idle periods so Azure LB doesn't kill the
        // socket. Filter them out before invoking onProgress so the UI doesn't see ghost updates.
        const { phase } = event;
        if (phase === 'heartbeat') continue;

        // `phase` is now narrowed to the consumer-facing PlanProgressPhase;
        // rebuild the dispatched event with it so the whole object satisfies
        // the onProgress PlanProgressEvent contract (the internal-only
        // `generation` field rides along harmlessly via the spread).
        onProgress({ ...event, phase });

        if (event.phase === 'complete') {
          if (event.plan) {
            // event.plan is the loose Record transport type (it must match the
            // interface's onProgress contract). Re-run it through transformKeys
            // to land the precise persisted-plan shape — the same boundary the
            // bare/wrapped GET paths use — rather than asserting across the gap.
            const d = transformKeys<RawPlanPayload>(event.plan);
            finalPlan = {
              id: d.id,
              workspaceId: d.workspaceId || workspaceId,
              status: d.status || 'draft',
              version: d.version || 1,
              createdAt: d.createdAt || '',
              updatedAt: d.updatedAt || '',
              items: this.normalizePlanItems(d.items || []),
              templateStyleStatus: d.templateStyleStatus ?? null,
              templateStyleWarning: d.templateStyleWarning ?? null,
            };
          } else {
            // A `complete` frame with no inline plan: read it back. A transient read failure
            // here is NOT terminal and is handled as its own known case — the post-stream
            // poller below re-runs the SAME GET twelve times, so leaving `finalPlan` null
            // hands the retry to the path that owns it. This preserved the pre-2026-08-23
            // behaviour deliberately: it used to be provided by the over-broad catch that also
            // swallowed the terminal refusal, and narrowing that catch would otherwise have
            // turned a recoverable read blip into a hard failure.
            try {
              const fetched = await this.getPlan(workspaceId);
              finalPlan = fetched.plan;
            } catch (fetchErr) {
              console.warn(
                '[generatePlanStream] complete event carried no plan and the follow-up GET failed; deferring to the post-stream poller:',
                fetchErr,
              );
            }
          }
        }

        if (event.phase === 'error' || event.phase === 'plan_failed') {
          // Mirror the chat-stream + regen-stream sibling pattern: throw
          // BennieApiError carrying the full structured envelope so the catch
          // boundary in WorkspacePage can discriminate terminal BE failures
          // from transient SSE drops and surface the structured message.
          // transformKeys is idempotent here (the event is already camelCased)
          // and yields the Record<string, unknown> body shape the error wants.
          //
          // The body carries `errorClass` (BE `error_class`) — the machine code
          // WorkspacePage keys `workspace:page.planGenError.<class>` on. `message`
          // rides along as ADR-043 defensive English and is never the discriminator.
          throw new BennieApiError(
            event.message || 'Plan generation failed',
            500,
            transformKeys<Record<string, unknown>>(event),
            `/workspaces/${workspaceId}/plan/generate-stream`,
          );
        }
      }
    }

    // If stream ended without complete event, poll with retries
    if (!finalPlan) {
      for (let attempt = 0; attempt < 12; attempt++) {
        try {
          const fetched = await this.getPlan(workspaceId);
          if (fetched.plan && fetched.plan.id) {
            finalPlan = fetched.plan;
            break;
          }
        } catch (retryErr) {
          console.warn('[generatePlanStream] Plan fetch retry failed:', retryErr);
        }
        await new Promise(r => setTimeout(r, 5000));
      }
    }

    if (!finalPlan) {
      throw new Error('Stream ended without a complete plan');
    }

    return { plan: finalPlan };
  }

  async approvePlan(workspaceId: string, planId: string, version?: number): Promise<{ plan: Plan }> {
    // T-12: transformer converts plan_id→planId automatically
    const result = await this.fetch<{ planId: string; status: string; version: number; plan: Plan }>(
      `/workspaces/${workspaceId}/plan/${planId}/approve`, {
        method: 'POST',
        body: version ? JSON.stringify({ version }) : undefined,
      }
    );

    if (result.plan) {
      return { plan: { ...result.plan, status: 'approved' } };
    }

    throw new Error('Plan approval succeeded but response missing plan data');
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Loop 2: Source Data Preparation
  // ─────────────────────────────────────────────────────────────────────────────

  async listWorkspaceFiles(workspaceId: string): Promise<WorkspaceFile[]> {
    // T-12: transformer handles all snake_case→camelCase conversion
    // Bounded by WORKSPACE_CRUD_TIMEOUT_MS (2026-08-28) — a hydration leg; `this.fetch`
    // applies no bound at all when the third argument is omitted.
    const data = await this.fetch<{
      files: RawWorkspaceFile[];
      count: number;
    }>(`/workspaces/${workspaceId}/files`, {}, WORKSPACE_CRUD_TIMEOUT_MS);

    return (data.files || []).map((f): WorkspaceFile => ({
      id: f.id ?? f.fileId ?? '',
      workspaceId,
      kind: (f.kind ?? f.category ?? 'source_data') as WorkspaceFile['kind'],
      filename: f.filename ?? f.fileName ?? '',
      fileType: f.fileType || f.contentType || '',
      uploadedAt: f.uploadedAt || f.createdAt || '',
      status: (f.processingStatus ?? f.status ?? 'pending') as WorkspaceFile['status'],
      contentDescription: f.contentDescription,
      dataType: f.dataType,
      keyTopics: f.keyTopics,
      hasTables: f.hasTables,
      tableCount: f.tableCount,
      columnNames: f.columnNames,
      rowCount: f.rowCount,
      retrievalHint: f.retrievalHint,
      sourceSections: f.sourceSections,
      localPath: f.localPath,
      blobUrl: f.blobUrl,
      enrichmentStatus: f.enrichmentStatus,
      // WI-14: carry the hard-reject reason CODE (closed set) so the honest-reject notice can render
      // WHY a file was rejected, from refetched persisted state. Resolved to words by the FE.
      rejectionReason: f.rejectionReason,
      // TD-1128: the persisted layout-analysis outcome, so a ready file whose layout failed shows a warning.
      layoutAnalysisStatus: f.layoutAnalysisStatus,
      // CR16 F-004 / TD-317: carry the failure STAGE (a closed vocabulary) so consumers can say WHY
      // a file needs review. The `enrichmentError` free-text sibling is FULL-DELETED: it was mapped
      // into memory here and read by NOTHING, while `GET /workspaces/{id}/files` returns the catalog
      // doc unprojected — so this line was the landing site of TD-323's measured wire leak (raw
      // Azure SDK exception text reaching the browser). No writer sets the field any more.
      enrichmentErrorStage: f.enrichmentErrorStage,
      // Librarian (Phase 3) graph fields. Populated by LibrarianRunner; null/absent on
      // workspaces where the librarian never ran or the run was pre-P2.
      cohort: f.cohort ?? null,
      relationships: f.relationships,
      graphStatus: f.graphStatus,
      graphComputedAt: f.graphComputedAt,
      emailSubject: f.emailSubject,
      emailFrom: f.emailFrom,
      emailTo: f.emailTo,
      emailDate: f.emailDate,
      parentFileId: f.parentFileId,
      isEmailAttachment: f.isEmailAttachment,
      metadata: {
        ...f.metadata,
        // Promote coversSections + source to metadata for the consumers that read them
        // from persisted GET /files state (evidence map, file badges).
        // L6: Dead snake_case fallbacks removed — responseTransformer already converts all keys
        // TD-514: `chartSpec` and `visualizationOf` were promoted here too and are FULL-DELETED.
        // Both arrived only on files carrying `source='test_visualization'`, which nothing has
        // written since W2 / WI-12 (2026-05-20). `chartSpec` fed the WorkspacePage chartSpecs
        // map (also deleted); `visualizationOf` never had a consumer at all — it was promoted
        // into metadata and read by nothing, in any language, anywhere in the repo.
        ...(f.coversSections ? { coversSections: f.coversSections } : {}),
        ...(f.source ? { source: f.source } : {}),
        // WI-11 (ADR-056): promote the persisted top-level origin='mdr' marker into
        // metadata.origin so DataSourceCard/FileMetadataDialog read it the same way
        // they read metadata.source — from persisted GET /files state, never optimistic.
        ...(f.origin ? { origin: f.origin } : {}),
        // F-audit-r5-02: this projection lists its fields, so a top-level `audit_phase` not named
        // here was DROPPED — every auditing file then carried no phase tag, both phase tabs
        // filtered to zero files, and the data panel lost its Metadata re-entry to the review
        // dialog (and its Ingest Data button). Promoted like `origin`.
        ...(f.auditPhase ? { audit_phase: f.auditPhase } : {}),
      },
    }));
  }

  async deleteFile(workspaceId: string, fileId: string): Promise<{ success: boolean; reingestRequired?: boolean }> {
    const endpoint = `/workspaces/${workspaceId}/files/${fileId}`;
    // M-6: cap the delete roundtrip so a hung connection can't spin forever.
    const response = await this.withTimeout(60_000, undefined, (signal) =>
      fetch(`${this.baseUrl}/api${endpoint}`, { method: 'DELETE', signal })
    );
    if (!response.ok) {
      // M-5 (client half): mirror fetch()/fetchFormData — preserve the structured
      // BE body so deletion_incomplete / delete_not_allowed / file_delete_failed
      // reach the FE with their error code + error_args (localized via errors.json),
      // instead of dropping them behind a generic `Failed to delete file: <status>`.
      const errorBody = await response.json().catch(() => ({ detail: response.statusText }));
      const message = localizeErrorBody(errorBody, response.status);
      throw new BennieApiError(message, response.status, errorBody, endpoint);
    }
    return this.parseJson(response);
  }

  // BUG-006 Gap 2: retry a file that has extraction_failed or indexing_failed status.
  // TD-217: throws the structured BennieApiError (mirrors deleteFile) so callers can branch
  // on the typed 409 `workspace_reingest_required` gate — the workspace is terminally
  // data_ingest_failed and the sanctioned recovery is the whole-WS re-ingest via
  // approveMetadata, not this endpoint. Previously this threw a bare `HTTP {status}` string,
  // which forced callers into a dead-end error toast.
  async retryFile(workspaceId: string, fileId: string): Promise<void> {
    const endpoint = `/workspaces/${workspaceId}/files/${fileId}/retry`;
    const response = await fetch(`${this.baseUrl}/api${endpoint}`, {
      method: 'POST',
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ detail: response.statusText }));
      const message = localizeErrorBody(errorBody, response.status);
      throw new BennieApiError(message, response.status, errorBody, endpoint);
    }
  }

  // Files at or above this size route through the direct-to-blob SAS path.
  // The SWA / Container Apps proxy caps multipart bodies at ~30 MB observed
  // (52 MB rejected with 413, 27 MB passed during the 2026-05-07 NLPC test).
  // The threshold is well below the cap to leave headroom for multipart
  // boundaries, transfer-encoding overhead, and clock skew on the SAS expiry.
  private static readonly DIRECT_UPLOAD_THRESHOLD_BYTES = 25 * 1024 * 1024;

  // TD-1067 (review round 2): NO hardcoded mirror of the BE's max-upload config. Round 1 hardcoded
  // 256 MB here — a guess at "the Search SKU's per-blob indexer limit" that was independently wrong
  // (S1's real limit is 128 MB) AND unable to track the per-env config even if the guess had been
  // right, because nothing forced the two numbers to agree. The served value from
  // POST /files/source-data/init (`max_size_bytes`, <SourceDataUploadLimits.MaxSizeBytes> on the
  // BE) is now the ONLY source: learned opportunistically from the first successful init response
  // this client instance sees, applied to every LATER file in this or a subsequent batch so a big
  // file still gets rejected with zero network calls once the limit is known. Before it is known
  // (the very first SAS-routed file in a session), there is nothing to pre-check against — the
  // per-file init call itself is both the check AND the source, and the BE remains authoritative
  // regardless (it enforces server-side whether or not this cache is populated).
  private knownSourceDataMaxUploadBytes: number | undefined;

  async uploadSourceFiles(workspaceId: string, files: File[], signal?: AbortSignal, archiveProvenance?: Record<string, { sourceArchive: string; archiveId: string }>, auditPhase?: AuditPhase): Promise<UploadFilesResult> {
    // Per-file isolation: a 413 on one file must not abort the rest of the
    // batch. Each upload now lands or fails on its own; the caller surfaces
    // partial-success cleanly. Above the proxy size cap we route through the
    // SAS path so the file body bypasses both the SWA proxy and Container Apps
    // ingress entirely.
    //
    // C3-063: optional `signal` threads user-cancel from the FE upload kick all
    // the way down to each per-file fetch. When the caller aborts mid-batch we
    // surface the AbortError up by rethrowing — partial-success bookkeeping is
    // NOT recorded for the cancelled batch because the user-intent is "stop"
    // and treating partial state as "uploaded but interrupted" would mask the
    // cancel signal at the UI layer. The per-file try/catch below distinguishes
    // genuine upload failures (which join `failed[]`) from abort (which bubbles).
    const uploaded: WorkspaceFile[] = [];
    const failed: UploadFilesResult['failed'] = [];
    // FIX-B (ADR-056): set true if any per-file source-data response reports a
    // failed MDR promotion. Surfaced as a non-blocking warning by the caller.
    let mdrPromotionFailed = false;

    for (const file of files) {
      // C3-063: short-circuit if the user cancelled between files. Re-checking
      // before each per-file roundtrip closes the gap where N-1 finished, the
      // user clicked Cancel, and file N would otherwise have been started.
      if (signal?.aborted) {
        throw new DOMException('Upload cancelled by user', 'AbortError');
      }

      const fileName = file.webkitRelativePath || file.name;

      // TD-1067 (review round 2): reject an over-limit file with NO network call, but only once the
      // limit is KNOWN from a served value — never from a hardcoded guess. `knownSourceDataMaxUploadBytes`
      // is populated by `initDirectUpload` the first time this client instance sees a successful init
      // response; until then there is nothing to pre-check against, and the per-file init call the SAS
      // route makes anyway is the check (the BE enforces server-side regardless of this cache).
      if (this.knownSourceDataMaxUploadBytes !== undefined && file.size > this.knownSourceDataMaxUploadBytes) {
        const limitMb = Math.floor(this.knownSourceDataMaxUploadBytes / (1024 * 1024));
        const errorCode = 'upload_too_large';
        const error = resolveApiError({ error: errorCode, error_args: { filename: fileName, limit_mb: limitMb } });
        failed.push({ fileName, error, status: undefined, errorCode });
        continue;
      }

      const useDirect = file.size >= HttpBennieClient.DIRECT_UPLOAD_THRESHOLD_BYTES;

      try {
        // ADR-084: provenance is keyed by File.name (the full relative path), which is
        // stable across the caller's DataTransfer rebuild — object identity is not.
        const prov = archiveProvenance?.[file.name];
        const result = useDirect
          ? await this.uploadOneViaSas(workspaceId, file, fileName, signal, prov, auditPhase)
          : await this.uploadOneViaMultipart(workspaceId, file, fileName, signal, prov, auditPhase);
        // FIX-B (ADR-056): the MDR promotion runs once, at the first source-data
        // upload (multipart seam only). Lift the transient mdrPromotionFailed flag
        // off the per-file result into the batch-level result, then strip it so the
        // transient notice never reaches rendered file state (persist-then-render).
        if (result.metadata?.mdrPromotionFailed === true) {
          mdrPromotionFailed = true;
          // Strip ONLY the transient flag — preserve any other metadata the BE
          // source-data response may carry now or later (defensive against schema
          // growth at this seam); drop the container only once it is empty.
          const remainingMeta = { ...result.metadata };
          delete remainingMeta.mdrPromotionFailed;
          result.metadata = Object.keys(remainingMeta).length > 0 ? remainingMeta : undefined;
          uploaded.push(result);
        } else {
          uploaded.push(result);
        }
      } catch (e) {
        // C3-063: AbortError is the user-cancel signal — rethrow so the caller
        // hits the abort branch and clears UI state. Any other failure is a
        // genuine per-file upload error that joins `failed[]` for partial-
        // success reporting (per the comment above).
        if (e instanceof DOMException && e.name === 'AbortError') {
          throw e;
        }
        if (e instanceof Error && e.name === 'AbortError') {
          throw e;
        }
        const status = e instanceof BennieApiError ? e.status : undefined;
        // Preserve the BE-declared error code (ADR-043 `error` field on the raw
        // body) so the caller can branch on it without string-matching the
        // localized message. e.g. a 409 `upload_not_allowed` is a state-gate
        // block (file already stored), NOT a lost-file failure.
        const errorCode = e instanceof BennieApiError && typeof e.body?.error === 'string'
          ? e.body.error
          : undefined;
        const error = e instanceof Error ? e.message : String(e);
        failed.push({ fileName, error, status, errorCode });
      }
    }

    return { uploaded, failed, ...(mdrPromotionFailed ? { mdrPromotionFailed: true } : {}) };
  }

  private async uploadOneViaMultipart(
    workspaceId: string,
    file: File,
    fileName: string,
    signal?: AbortSignal,
    archiveProvenance?: { sourceArchive: string; archiveId: string },
    auditPhase?: AuditPhase
  ): Promise<WorkspaceFile> {
    const formData = new FormData();
    // Pass webkitRelativePath when the file came from a folder upload so the
    // backend can extract local_path (FileEndpoints.cs, grep the comment
    // `Row 55: Extract local_path from folder uploads` — L3509-L3512 at
    // 6a302d9eb, immediately after the MDR promote block in the same
    // multipart handler). File.name is a basename even for webkitdirectory
    // uploads — the relative path lives on webkitRelativePath only.
    //
    // Citation corrected 2026-08-20 (WI-36): this read `FileEndpoints.cs:93-97`,
    // which is `ReleaseWorkflowSlotAsync` — a workflow-slot helper with nothing
    // to do with local_path. Anchored on a content phrase so the next drift in a
    // 9,092-line file is survivable.
    formData.append('file', file, fileName);
    // ADR-084: stamped ONLY for expanded archive members — an ordinary upload sends
    // neither field, so the BE writes neither catalog key (no empty-string stamps).
    if (archiveProvenance) {
      formData.append('source_archive', archiveProvenance.sourceArchive);
      formData.append('archive_id', archiveProvenance.archiveId);
    }
    // Cycle 5 auditing rework: the multipart handler reads `audit_phase` off the form and writes
    // the normalised short tag onto the catalog row. Appended ONLY when the caller declared one —
    // the endpoint REFUSES the field on a non-auditing workspace, so an unconditional append would
    // break every other service type's upload.
    if (auditPhase) {
      formData.append('audit_phase', auditPhase);
    }

    // T-12: transformer converts file_id→fileId, file_name→fileName automatically
    // C3-063: signal threads user-cancel into the multipart POST roundtrip.
    // FIX-B (ADR-056): the BE source-data response now optionally carries
    // mdr_promotion_failed (transformed mdrPromotionFailed) when the MDR promotion
    // (model_development_report→source_data) failed at this first-batch seam
    // (FileEndpoints.cs, grep `MdrPromotionFailed = mdrPromotionFailed` — L3679 at
    // 6a302d9eb; the citation here read `:546`, which is unrelated
    // completeness-ladder prose — corrected 2026-08-20 / WI-36, the third stale
    // FileEndpoints.cs pin found in this file). Surfaced as a non-blocking warning
    // by the caller — it never fails the source upload itself. Note this flag is
    // only ever set on the MULTIPART arm; uploadOneViaSas has no MDR promotion and
    // so cannot report a failure of one.
    const response = await this.fetchFormData<{
      fileId: string;
      fileName: string;
      blobUrl: string;
      category: string;
      status: string;
      mdrPromotionFailed?: boolean;
    }>(`/workspaces/${workspaceId}/files/source-data`, formData, signal);

    return {
      id: response.fileId,
      workspaceId,
      kind: 'source_data',
      filename: response.fileName,
      uploadedAt: new Date().toISOString(),
      status: 'uploaded' as WorkspaceFile['status'],
      // Transient transport channel for the FIX-B warning. Aggregated + stripped by
      // uploadSourceFiles into UploadFilesResult.mdrPromotionFailed; this is the
      // upload-response notice path, NOT persisted workspace state.
      ...(response.mdrPromotionFailed ? { metadata: { mdrPromotionFailed: true } } : {}),
    };
  }

  private async uploadOneViaSas(
    workspaceId: string,
    file: File,
    fileName: string,
    signal?: AbortSignal,
    archiveProvenance?: { sourceArchive: string; archiveId: string },
    auditPhase?: AuditPhase
  ): Promise<WorkspaceFile> {
    // Step 1 — ask the API for a write SAS URL.
    // C3-063: signal threads user-cancel into init / direct PUT / finalize.
    const init = await this.initDirectUpload(workspaceId, {
      fileName,
      contentType: file.type || undefined,
      sizeBytes: file.size,
      // Sent on init even though init persists nothing: it is validated there so a SAS is never
      // minted for an upload the finalize leg would go on to refuse AFTER the bytes are already in
      // blob storage. Refusing early is the whole reason the field exists on this leg.
      auditPhase,
    }, signal);

    // Step 2 — PUT the body straight at Azure Blob Storage. `x-ms-blob-type:
    // BlockBlob` is required by the Azure REST contract; `x-ms-version` pins
    // the API surface so a later default version bump can't break the upload.
    const headers: Record<string, string> = {
      'x-ms-blob-type': 'BlockBlob',
      'x-ms-version': '2023-11-03',
    };
    if (file.type) {
      headers['x-ms-blob-content-type'] = file.type;
    }
    // M-6: cap the direct-to-blob PUT at 600s (large files) so a stalled upload
    // can't hang the batch forever. A timeout rejects with TimeoutError (a real
    // failure routed to `failed[]`), distinct from the cancel-AbortError swallow.
    // TD-1119: deliberately longer than the 10-min SAS — the SAS bounds only when this PUT may
    // START (Azure checks expiry at request start); the transfer itself may run past it.
    const putResp = await this.withTimeout(600_000, signal, (composed) =>
      fetch(init.uploadUrl, {
        method: 'PUT',
        headers,
        body: file,
        signal: composed,
      })
    );
    if (!putResp.ok) {
      const text = await putResp.text().catch(() => '');
      throw new BennieApiError(
        `Direct blob upload failed: HTTP ${putResp.status}${text ? ` — ${text.slice(0, 200)}` : ''}`,
        putResp.status,
        {},
        init.uploadUrl
      );
    }

    // Step 3 — tell the API to register the now-uploaded blob.
    return await this.finalizeDirectUpload(workspaceId, {
      fileId: init.fileId,
      fileName,
      blobName: init.blobName,
      contentType: file.type || undefined,
      sizeBytes: file.size,
      localPath: file.webkitRelativePath || undefined,
      sourceArchive: archiveProvenance?.sourceArchive,
      archiveId: archiveProvenance?.archiveId,
      auditPhase,
    }, signal);
  }

  async initDirectUpload(
    workspaceId: string,
    args: { fileName: string; contentType?: string; sizeBytes?: number; auditPhase?: AuditPhase },
    signal?: AbortSignal
  ): Promise<{ fileId: string; blobName: string; container: string; uploadUrl: string; expiresAt: string; maxSizeBytes: number }> {
    // M-6: the SAS-mint roundtrip is a small JSON call — cap at 60s so a hung
    // init can't stall the upload. Timeout surfaces as TimeoutError (real failure).
    const result = await this.withTimeout(60_000, signal, (composed) =>
      this.fetch<{
        fileId: string;
        blobName: string;
        container: string;
        uploadUrl: string;
        expiresAt: string;
        maxSizeBytes: number;
      }>(`/workspaces/${workspaceId}/files/source-data/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          file_name: args.fileName,
          content_type: args.contentType,
          size_bytes: args.sizeBytes,
          // Omitted (not null, not '') when the caller declared no phase — the endpoint refuses the
          // field outside an auditing workspace, and `undefined` drops out of JSON.stringify.
          audit_phase: args.auditPhase,
        }),
        signal: composed,
      })
    );

    // TD-1067 (review round 2): the SERVED value is the only source of the FE's size ceiling — fail
    // loud rather than silently proceeding with no known limit (which would be an invented "no
    // limit" default in every way that matters) if the BE ever omits or corrupts the field.
    if (!(typeof result.maxSizeBytes === 'number' && Number.isFinite(result.maxSizeBytes) && result.maxSizeBytes > 0)) {
      throw new Error(
        `The server did not report an upload size limit for this workspace, so the upload was not started. Please try again.`
      );
    }
    // Cache for every LATER file in this or a subsequent batch — see the field's own comment.
    this.knownSourceDataMaxUploadBytes = result.maxSizeBytes;

    return result;
  }

  async finalizeDirectUpload(
    workspaceId: string,
    args: {
      fileId: string;
      fileName: string;
      blobName: string;
      contentType?: string;
      sizeBytes: number;
      localPath?: string;
      // ADR-084 archive provenance — undefined for ordinary uploads.
      sourceArchive?: string;
      archiveId?: string;
      // Cycle 5 auditing rework — THIS is the leg that persists the phase tag.
      auditPhase?: AuditPhase;
    },
    signal?: AbortSignal
  ): Promise<WorkspaceFile> {
    // M-6: finalize is a small JSON registration call — cap at 60s so a hung
    // finalize can't leave the blob uploaded but the batch spinning. Timeout
    // surfaces as TimeoutError (real failure), not a swallowed cancel.
    const response = await this.withTimeout(60_000, signal, (composed) =>
      this.fetch<{
        fileId: string;
        fileName: string;
        blobUrl: string;
        category: string;
        status: string;
      }>(`/workspaces/${workspaceId}/files/source-data/finalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          file_id: args.fileId,
          file_name: args.fileName,
          blob_name: args.blobName,
          content_type: args.contentType,
          size_bytes: args.sizeBytes,
          local_path: args.localPath,
          source_archive: args.sourceArchive,
          archive_id: args.archiveId,
          audit_phase: args.auditPhase,
        }),
        signal: composed,
      })
    );
    return {
      id: response.fileId,
      workspaceId,
      kind: 'source_data',
      filename: response.fileName,
      uploadedAt: new Date().toISOString(),
      status: 'uploaded' as WorkspaceFile['status'],
    };
  }

  async rerunCompleteness(workspaceId: string, planId: string, scope?: CompletenessScope): Promise<void> {
    await this.fetch(
      `/workspaces/${workspaceId}/plan/${planId}/completeness/rerun${completenessScopeQuery(scope)}`,
      { method: 'POST' },
    );
  }

  // ── Cycle 5 auditing rework (WI-11) — runs and report versions ────────────────────────────────
  //
  // 🔴 THESE FOUR READ THE WIRE BODY RAW. Every other method on this client goes through
  // `transformKeys` (snake_case → camelCase); these deliberately do not, because an auditing run's
  // `results` is a MAP KEYED BY REGISTER-ITEM ID. A blanket key transform cannot tell a field name
  // from a data key, so it would rewrite `C-01` / `P-4.2` as if they were properties — silently,
  // and in a way that only shows up as an item view that cannot find its own result.

  async listAuditRuns(workspaceId: string, phase?: AuditPhase, signal?: AbortSignal): Promise<AuditRun[]> {
    const qs = phase ? `?phase=${encodeURIComponent(phase)}` : '';
    const body = await this.fetchRaw<{ runs?: AuditRun[] }>(
      `/workspaces/${workspaceId}/audit/runs${qs}`, { signal }, 30_000);
    // A malformed 200 is a failure, not an empty roster: `[]` means "this phase has never run",
    // which is a real and differently-rendered state. Coercing a broken body into it would put an
    // "no runs yet" empty screen over a transport defect.
    if (!Array.isArray(body?.runs)) {
      throw new BennieApiError('audit_runs_malformed_response', 200, body as Record<string, unknown>,
        `/workspaces/${workspaceId}/audit/runs`);
    }
    return body.runs;
  }

  async getAuditRun(workspaceId: string, runId: string, signal?: AbortSignal): Promise<AuditRun> {
    return await this.fetchRaw<AuditRun>(
      `/workspaces/${workspaceId}/audit/runs/${runId}`, { signal }, 30_000);
  }

  async triggerAuditRun(workspaceId: string, phase: AuditPhase, signal?: AbortSignal): Promise<AuditRunTriggerAccepted> {
    return await this.fetchRaw<AuditRunTriggerAccepted>(
      `/workspaces/${workspaceId}/audit/runs`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phase }),
        signal,
      },
      60_000,
    );
  }

  async listReportVersions(workspaceId: string, signal?: AbortSignal): Promise<ReportVersionRoster> {
    const body = await this.fetchRaw<ReportVersionRoster>(
      `/workspaces/${workspaceId}/report-versions`, { signal }, 30_000);
    if (!Array.isArray(body?.report_versions)) {
      throw new BennieApiError('report_versions_malformed_response', 200, body as unknown as Record<string, unknown>,
        `/workspaces/${workspaceId}/report-versions`);
    }
    return body;
  }

  async getCompletenessReport(
    workspaceId: string, planId: string, _force?: boolean, scope?: CompletenessScope,
  ): Promise<CompletenessReport> {
    // GET completeness is pure-read. To trigger a new run, call rerunCompleteness.
    // The interface's optional `force` flag is accepted for backwards
    // compatibility with older callers; it has no effect on this pure-read GET,
    // so the implementation does not bind it.
    // T-12: transformer converts all snake_case keys (missing_data→missingData, coverage_verdict→coverageVerdict, etc.)
    const raw = await this.fetch<Record<string, unknown>>(
      `/workspaces/${workspaceId}/plan/${planId}/completeness${completenessScopeQuery(scope)}`);
    // Prep-round P-1: severity is now canonical int (0-3). Legacy docs may still
    // carry a string shape; pass-through and let `severityToMuiTone` handle both
    // at render time (single source of truth for severity → tone mapping).
    const missingData = (raw.missingData as Array<{ section: string; sectionId?: string; description: string; severity: number | string }>) ?? [];
    return {
      workspaceId,
      planId,
      createdAt: new Date().toISOString(),
      // The scoped dialog and recheck gate compare persisted assessments, not fetches.
      completenessCheckId: raw.completenessCheckId as string | undefined,
      generationStatus: raw.generationStatus as CompletenessReport['generationStatus'],
      inputGeneration: raw.inputGeneration as number | undefined,
      requestAttribution: raw.requestAttribution as CompletenessReport['requestAttribution'],
      missingItems: missingData.map((m, i) => ({
        id: `missing-${i}`,
        sectionId: m.sectionId,
        severity: m.severity,
        title: m.section,
        description: m.description,
      })),
      coverageVerdict: raw.coverageVerdict as CompletenessReport['coverageVerdict'],
      // CR5 Phase 4 F-005: overallScore deleted from contract — verdict drives FE gating.
      isComplete: raw.isComplete as boolean | undefined,
      criticalGaps: raw.criticalGaps as string[] | undefined,
      // ➕ TD-1037 (FE hop) — PROJECTED, where it previously fell on the floor.
      //
      // 🔴 Its absence here was invisible for as long as the only reader of `sectionCoverage` was
      // `useCompletenessPolling`, which hydrates `details` from WORKSPACE MESSAGE metadata and
      // never from this GET. The per-phase coverage read has no message channel — it asks this
      // endpoint directly — so a dropped field renders as "No coverage check has run for these
      // files" over a verdict the server did compute, which is the most plausible-looking wrong
      // screen available. `CompletenessCheckResult.section_coverage` is on the wire and
      // `transformKeys` has already camel-cased it by the time it reaches here.
      sectionCoverage: raw.sectionCoverage as CompletenessReport['sectionCoverage'],
      recommendations: raw.recommendations as string[] | undefined,
      suggestedUploads: raw.suggestedUploads as CompletenessReport['suggestedUploads'],
      testRunNeeded: raw.testRunNeeded as boolean | undefined,
      // CR6 Phase 6C F-005 Phase 2: testRunGuidance passthrough physically deleted.
    };
  }


  async enrichMetadata(workspaceId: string, descriptions?: Record<string, string>, options?: { signal?: AbortSignal }): Promise<{
    status: string;
    message?: string;
  }> {
    return this.fetch(`/workspaces/${workspaceId}/files/enrich-metadata`, {
      method: 'POST',
      body: JSON.stringify({ descriptions: descriptions ?? {} }),
      signal: options?.signal,
    });
  }

  async approveMetadata(workspaceId: string, fileEdits?: Record<string, Record<string, unknown>>, auditPhase?: AuditPhase, reingest?: boolean, approvalRequestId?: string): Promise<{
    status: string;
    indexerResults?: Record<string, unknown>;
  }> {
    return this.fetch(`/workspaces/${workspaceId}/files/approve-metadata`, {
      method: 'POST',
      body: JSON.stringify({ fileEdits: fileEdits ?? {}, ...(auditPhase ? { audit_phase: auditPhase } : {}), ...(reingest ? { reingest: true } : {}), ...(approvalRequestId ? { approval_request_id: approvalRequestId } : {}) }),
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Evidence Map (RCF-1 Phase 1D)
  // ─────────────────────────────────────────────────────────────────────────────

  async getEvidenceMap(workspaceId: string): Promise<EvidenceMapResponse> {
    return this.fetch<EvidenceMapResponse>(`/workspaces/${workspaceId}/evidence-map`);
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Loop 3: Section Generation & Review
  // ─────────────────────────────────────────────────────────────────────────────

  // W1-B: Section generation progress — mirrors plan generation progress pattern.
  // The BE serializes these counters snake_case; transformKeys renames them before the
  // caller sees the object, so the declared shape is camelCase. It was previously
  // declared snake_case, and usePlanProgress read it verbatim — pinning the "X of Y
  // complete" counter at 0 and the current-section name at undefined for whole runs,
  // while `total` / `phase` / `inflight` worked because they carry no underscore.
  async getSectionGenProgress(workspaceId: string, planId: string): Promise<{
    phase: string;
    contextPrepCompleted: number;
    contentGenCompleted: number;
    total: number;
    currentSectionId: string | null;
    currentSectionTitle: string | null;
    lastUpdated: string | null;
    // True iff at least one CancelSignalService CTS is registered for this workspace.
    // FE gates the Stop button on this — no other liveness inference is done client-side.
    inflight: boolean;
  }> {
    return this.fetch(`/workspaces/${workspaceId}/plan/${planId}/sections/generation-progress`);
  }

  async listSections(workspaceId: string, planId: string): Promise<Section[]> {
    // T-12: transformer converts section_id→sectionId, section_title→sectionTitle, etc.
    // Bounded by WORKSPACE_CRUD_TIMEOUT_MS (2026-08-28). ⚠️ THE OMISSION WAS NOT INERT: `this.fetch`
    // SKIPS `withTimeout` ENTIRELY when `timeoutMs` is undefined, so passing nothing was not "take
    // the default" — there is no default, and this read had no bound at all.
    const data = await this.fetch<{
      sections: RawSection[];
      total?: number;
      completed?: number;
    }>(`/workspaces/${workspaceId}/plan/${planId}/sections`, {}, WORKSPACE_CRUD_TIMEOUT_MS);

    return data.sections.map((s): Section => ({
      id: s.sectionId || s.id || '',
      workspaceId,
      planItemId: s.sectionId || s.id || '',
      title: s.sectionTitle ?? '',
      fullTitle: s.fullTitle,
      level: s.level,
      status: s.status === 'completed' ? 'validated'            // backend 'completed' → FE 'validated' (was 'generated', now removed)
            : s.status === 'generating' ? 'generating'           // ISS-18: was 'in_progress' — collapsed "Generating..." label
            : s.status === 'context_ready' ? 'context_ready'     // ISS-18: was falling to default
            : s.status === 'in_progress' ? 'in_progress'
            : s.status === 'approved' ? 'validated'              // legacy 'approved' → 'validated' (approved removed from SectionStatus)
            : s.status === 'failed' ? 'generation_failed'        // legacy 'failed' → 'generation_failed' (failed removed from SectionStatus)
            : s.status === 'generation_failed' ? 'generation_failed'
            : s.status === 'context_unavailable' ? 'context_unavailable'  // Batch 18 E-10 (PF64)
            : s.status === 'validated' ? 'validated'
            : s.status === 'pending_validation' ? 'pending_validation'
            : s.status === 'validating' ? 'validating'                   // F-PROD-01: BE flips sections to `validating` during holistic_validating — explicit case kills the per-poll console.warn spam
            : s.status === 'validation_skipped' ? 'validation_skipped'    // F-RUN-OOS-03 honest terminal
            : s.status === 'needs_regen' ? 'needs_regen'
            : s.status === 'pending_phase_2c' ? 'pending_phase_2c'           // ES sections deferred to Phase 2C (R6.1-F)
            : s.status === 'exec_summary_blocked' ? 'pending_phase_2c'      // legacy ES blocked → pending_phase_2c (exec_summary_blocked removed)
            : s.status === 'user_accepted_as_gap' ? 'user_accepted_as_gap'   // CR20 F-20-04
            : (() => {
                if (s.status && s.status !== 'pending') {
                  console.warn(`[HttpBennieClient] Unknown section status "${s.status}" for section ${s.sectionId || s.id} — preserving as-is`);
                }
                // Preserve an unrecognized BE status verbatim (logged above) rather
                // than coercing it — the cast acknowledges the deliberate widening.
                return (s.status || 'pending') as Section['status'];
              })(),
      sectionType: s.sectionType as 'report' | 'test_run' | 'table' | 'verbatim' | 'executive_summary' | 'reference',
      // F-MORNING-01: the live sections API serves `current_version` (camelized by the
      // T-12 transformer to `currentVersion`); `latestVersion` alone was a dead mapping
      // that left the field undefined and let the export handler's v1 default win.
      latestVersion: s.latestVersion ?? (s as { currentVersion?: number }).currentVersion,
      hasVersion: s.hasVersion,
      versionStatus: s.versionStatus,
      createdAt: s.createdAt,
      hasSubItems: s.hasSubItems,
      // CR6 Phase 3B (F-007): diagnostic fields. Backend serialises snake_case
      // (error_code etc.) — the upstream T-12 transformer already handles snake→camel
      // conversion at the JSON parse layer, so these arrive in camelCase. Optional —
      // success-path sections leave them undefined.
      errorCode: s.errorCode,
      errorMessage: s.errorMessage,
      errorStage: s.errorStage,
      retryCount: s.retryCount,
      failedAt: s.failedAt,
      lastErrorDetail: s.lastErrorDetail,
      figuresNotShownVersion: s.figuresNotShownVersion,
      // CR6 Phase 3C-Gate (F-008): blocker payload on ES root when status is
      // 'exec_summary_blocked'. Backend: blocking_sections → blockingSections via T-12.
      blockingSections: s.blockingSections,
    }));
  }

  async startGeneration(workspaceId: string, planId: string, mappingOverrides?: EvidenceMapping[]): Promise<void> {
    const options: RequestInit = { method: 'POST' };
    if (mappingOverrides && mappingOverrides.length > 0) {
      // Phase 1D: Convert array of EvidenceMapping to Dictionary<string, List<string>>
      // Backend expects { mappingOverrides: { "file_id_1": ["section_1", "section_3"], ... } }
      const overridesDict: Record<string, string[]> = {};
      for (const m of mappingOverrides) {
        overridesDict[m.fileId] = m.coversSections;
      }
      options.body = JSON.stringify({ mappingOverrides: overridesDict });
    }
    await this.fetch(`/workspaces/${workspaceId}/plan/${planId}/generate-sections`, options);
  }

  // The interface carries a `version` arg for symmetry with the version-pinned
  // approve flow; this endpoint approves the latest version server-side, so the
  // implementation does not bind it.
  async approveSection(workspaceId: string, planId: string, sectionId: string): Promise<void> {
    await this.fetch(`/workspaces/${workspaceId}/plan/${planId}/sections/${sectionId}/approve`, { method: 'POST' });
  }


  // WI-12: operator chooses to ship the section as a data gap. BE transitions
  // section.status to `user_accepted_as_gap`; Mark Complete eligibility unlocks
  // once every section is validated or accept-as-gap'd. Idempotent — repeated
  // calls return 409 from the BE eligibility check.
  // The DOCX render depends on whether the section already has content (operator
  // ruling 2026-08-08): none → the "Accepted — No Content" placeholder; content →
  // that content is KEPT and marked "Accepted — Not Reconciled". This call carries
  // no content either way — it writes status + reason only, never section_versions.
  async acceptAsGap(workspaceId: string, planId: string, sectionId: string, reason: string): Promise<void> {
    await this.fetch(`/workspaces/${workspaceId}/plan/${planId}/sections/${sectionId}/accept-as-gap`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
  }


  async refirePhase2C(workspaceId: string, planId: string): Promise<Phase2CRefireResponse> {
    return await this.fetch<Phase2CRefireResponse>(
      `/workspaces/${workspaceId}/plan/${planId}/phase2c/refire`,
      { method: 'POST' }
    );
  }

  // WI-PF-05 Approach B (B-3): ONE POST to the consolidated bulk-regen endpoint. fetch<T>
  // throws the typed BennieApiError on 409 (export/terminal/halt pre-guard) and 503
  // (bulk_regen_unavailable) with a localized, user-friendly message; the 2xx JSON ack
  // ({ regenerating, alreadyRunning?, message }) is returned for a single caller-side notice.
  // Progress is NEVER read from this payload — the caller polls persisted section status.
  async regenerateAllFailed(workspaceId: string, planId: string): Promise<RegenerateAllFailedResponse> {
    return await this.fetch<RegenerateAllFailedResponse>(
      `/workspaces/${workspaceId}/plan/${planId}/regenerate-all-failed`,
      { method: 'POST' }
    );
  }

  // IMP-017: Cancel an in-progress batch section generation
  async cancelGeneration(workspaceId: string, planId: string): Promise<{ status: string; recoveredSections: number }> {
    return await this.fetch<{ status: string; recoveredSections: number }>(
      `/workspaces/${workspaceId}/plan/${planId}/cancel-generation`,
      { method: 'POST' }
    );
  }

  async getSectionVersions(workspaceId: string, planId: string, sectionId: string): Promise<SectionVersion[]> {
    const data = await this.fetch<{
      success: boolean;
      sectionId: string;
      versions: Array<{
        // The persisted row id (`{sectionId}_v{N}`). Always on the wire — every SectionVersion
        // writer sets it — and now mapped through, because the inline-edit save echoes it back as
        // `base_version_id` for the API's 409 concurrency check.
        id: string;
        version: number;
        content: string;
        status: 'draft' | 'generated' | 'approved';
        createdAt: string;
        feedback?: string;
        // Provenance label for the row, written by the backend that persisted it. The
        // canonical vocabulary lives in apps/api — do not maintain a copy of it here. The
        // copy that used to sit in this comment enumerated eight labels, and by 2026-08-12
        // three of them (`holistic_regen_empty`, `holistic_regen_failed`,
        // `holistic_regen_timeout`) matched nothing anywhere under apps/api; a list kept in
        // the client is a list that drifts without anything failing.
        // F-VER-SELECTOR-2 (2026-05-18) added the field to the mapper, which had been
        // dropping it. It is threaded because it is part of the payload, NOT because any
        // consumer discriminates on it: the version pager used to subtract `holistic_regen`
        // rows from its total and no longer does — see ADR-020 Amendment 1 (2026-08-12).
        source?: string;
        citations?: Array<{
          id: string;
          source: { filename: string; page?: number };
          excerpt: string;
        }>;
        artifacts?: SectionVersion['artifacts'];
      }>;
      // TD-438: capability URLs for every `ckfigure://` reference across this section's
      // versions, minted by the CK plane during THIS request. A list of pairs, not an object
      // map — see CkFigureUrlPair for why the key transform makes the object form unusable.
      ckFigures?: CkFigureUrlPair[];
    }>(`/workspaces/${workspaceId}/plan/${planId}/sections/${sectionId}/versions`);

    // One map for the section, attached to every version: lookups are by exact reference, so
    // the union is correct for whichever version the picker renders.
    const ckFigureUrls = toCkFigureUrlMap(data.ckFigures);

    return data.versions.map((v) => ({
      id: v.id,
      version: v.version,
      content: v.content,
      status: (v.status === 'draft' ? 'validated' : v.status) as SectionVersion['status'],
      createdAt: v.createdAt,
      feedback: v.feedback,
      source: v.source,
      citations: v.citations,
      artifacts: v.artifacts,
      ckFigureUrls,
    }));
  }

  /**
   * Registry task #16: `baseVersionId` names the persisted version the editor was opened on, and
   * the API REQUIRES it — absent is 400 `edit_base_version_required`, moved-underneath is 409
   * `edit_base_version_stale` / `edit_concurrent_append`. Both surface through the standard
   * BennieApiError path, so the caller's existing catch renders the localized message.
   *
   * DEPLOY ORDER: this client must reach production BEFORE (or with) the API change. A NEW client
   * against the OLD API is harmless — `EditSectionRequest` carries no
   * `JsonUnmappedMemberHandling.Disallow`, so the extra member is ignored — while a new API against
   * an OLD client 400s every inline save.
   */
  async updateSectionContent(
    workspaceId: string, planId: string, sectionId: string, content: string, baseVersionId: string,
  ): Promise<SectionVersion> {
    // POST-transformKeys shape, like every `this.fetch` type in this file. The API emits
    // `version_id` on the wire (SectionEndpoints, task #16) and `transformKeys` deep-camelCases it
    // before this type is applied, so `versionId` is the LIVE read and `version_id` would be the
    // dead one — the exact inversion the WALK-DEV dead-read wave fixed. Both directions are pinned
    // by `workspaceWireReads.test.tsx`, which feeds the real snake_case body through this method.
    // The REQUEST body below is not transformed by anything, so it stays snake_case outbound.
    const data = await this.fetch<{
      success: boolean;
      versionId: string;
      version: number;
      content: string;
      createdAt: string;
    }>(`/workspaces/${workspaceId}/plan/${planId}/sections/${sectionId}/edit`, {
      method: 'POST',
      body: JSON.stringify({ content, base_version_id: baseVersionId }),
    });

    return {
      id: data.versionId,
      version: data.version,
      content: data.content,
      status: 'validated' as const,
      createdAt: data.createdAt,
    };
  }

  /**
   * Cycle 5 WI-07 — save a MANUAL PROSE EDIT of one plan item.
   *
   * `fields` carries ONLY the four editable fields, and only the ones actually changed. An absent
   * key leaves the stored value alone; a key the server does not recognise is refused with
   * `plan_edit_structural_change`, so there is nothing to be gained by sending more.
   *
   * `basePlanVersion` is the plan version the editor OPENED on, and the API requires it — absent is
   * 400 `plan_edit_base_version_required`, moved-underneath is 409 `version_conflict`. It is never
   * defaulted to the current latest here either: a client that re-read the latest before saving
   * would defeat the check on the client side just as thoroughly as the server defaulting it.
   *
   * **RETURNS NOTHING — persist-then-render.** The route answers with the refetched plan, and
   * handing the caller that payload invites a render from a response body, which `frontend-rules`
   * forbids. The caller refetches the plan and draws THAT.
   *
   * Refusal vocabulary, all through the standard `BennieApiError` path so the caller's existing
   * catch renders the localized message: `plan_edit_plan_approved`, `plan_edit_structural_change`,
   * `plan_edit_enrichment_emptied`, `plan_edit_title_template_locked`, `plan_edit_no_fields`,
   * `plan_edit_ids_renumbered`, `version_conflict`, `plan_not_found`, `plan_item_not_found`.
   */
  async updatePlanItemProse(
    workspaceId: string, planId: string, itemId: string,
    fields: PlanItemProseFields, basePlanVersion: number,
  ): Promise<void> {
    // Outbound bodies in this file are NOT key-transformed, so the wire spelling is written here.
    const body: Record<string, string> = {};
    if (fields.title !== undefined) body.title = fields.title;
    if (fields.description !== undefined) body.description = fields.description;
    if (fields.method !== undefined) body.method = fields.method;
    if (fields.expectedOutput !== undefined) body.expected_output = fields.expectedOutput;

    await this.fetch<unknown>(
      `/workspaces/${workspaceId}/plan/${planId}/items/${encodeURIComponent(itemId)}/edit`,
      { method: 'POST', body: JSON.stringify({ fields: body, base_plan_version: basePlanVersion }) },
    );
  }

  /**
   * B13 WI-73 — read the PERSISTED phase of a live section regeneration.
   *
   * Note what this deliberately does NOT do: it does not swallow a read failure into a
   * benign-looking `{ phase: 'idle' }`. That would make a 500 indistinguishable from
   * "nothing is running" — the same maskover WI-71 removed from `getPlanGenProgress`,
   * and worse here, because `idle` is an affirmative claim that the operation STOPPED.
   * A failed read throws and the caller decides; only the SERVER may say `idle`.
   */
  async getSectionRegenProgress(workspaceId: string, planId: string, sectionId?: string): Promise<SectionRegenProgress> {
    // ⚖️ R-B4 (2026-09-05): name the section when the caller knows it. The server resolves the
    // caller's own `section_regen_{id}` lease first, then the fleet lease, then any live per-section
    // lease — so an un-updated caller still learns that regeneration is happening rather than being
    // told `idle`, and an updated one gets ITS operation's phase rather than a sibling's.
    const query = sectionId ? `?sectionId=${encodeURIComponent(sectionId)}` : '';
    const url = `${this.baseUrl}/api/workspaces/${workspaceId}/plan/${planId}/sections/regen-progress${query}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`section regen-progress read failed: HTTP ${response.status}`);
    }
    const data = await this.parseJson<{
      phase?: string;
      phaseMessageCode?: string | null;
      phaseAt?: string | null;
      sectionId?: string | null;
      planId?: string | null;
      startedAt?: string | null;
      lastHeartbeat?: string | null;
      inflight?: boolean;
    }>(response);
    return {
      // A response with no `phase` is a contract violation, not an idle workspace, so it
      // resolves to `unknown` (running, position not reported) rather than to `idle`.
      // Getting this default wrong is how a live operation gets reported as stopped.
      phase: data.phase ?? 'unknown',
      phaseMessageCode: data.phaseMessageCode ?? null,
      phaseAt: data.phaseAt ?? null,
      sectionId: data.sectionId ?? null,
      planId: data.planId ?? null,
      startedAt: data.startedAt ?? null,
      lastHeartbeat: data.lastHeartbeat ?? null,
      inflight: data.inflight === true,
    };
  }

  async requestSectionRevision(
    workspaceId: string, planId: string, sectionId: string, feedback: string,
    onProgress?: (phase: string, message: string) => void,
    // CR20 F-20-18: caller-declared regen intent. Matches BE RegenMode:
    //   'bulk_retry'          — "Regenerate all failed" button (no feedback; lands at pending_validation)
    //   'retry_after_failure' — single-section retry button on a generation_failed section
    //   'holistic_flagged'    — holistic validator flagged the section for regen with guidance
    //   'user_feedback'       — chat feedback regen (default; section → validated after regen)
    // Prior param was `source?: 'bulk_retry' | 'feedback'` which the BE expanded via inference.
    // Promoting to explicit intent per no-deferrals rule — BE no longer infers.
    intent?: 'bulk_retry' | 'retry_after_failure' | 'holistic_flagged' | 'user_feedback',
  ): Promise<SectionVersion> {
    const url = `${this.baseUrl}/api/workspaces/${workspaceId}/plan/${planId}/sections/${sectionId}/regenerate`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ instruction: feedback, intent }),
    });

    if (!response.ok) {
      // CR16 F-014: BennieApiError carries structured {error, category, message}
      // so handleRetryFailed/handleBulkRegen can branch on api.body.error ===
      // 'infrastructure_halt' and route the user to the halt-clear CTA.
      const errorBody = await response.json().catch(() => ({ detail: response.statusText }));
      const message = localizeErrorBody(errorBody, response.status);
      throw new BennieApiError(message, response.status, errorBody, `/workspaces/${workspaceId}/plan/${planId}/sections/${sectionId}/regenerate`);
    }

    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result: RegenStreamEvent | null = null;

    const READ_TIMEOUT_MS = 180_000;

    // Batch 02 NEW-008: per-call generation tracker.
    // Local variable scoped to this single requestSectionRevision invocation. Each call starts fresh
    // (lastKnownGeneration = 0), so there is no cross-session state to reset on navigation or unmount.
    let lastKnownGeneration = 0;

    for (;;) {
      let readResult: ReadableStreamReadResult<Uint8Array>;
      try {
        readResult = await Promise.race([
          reader.read(),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Section regeneration stream stalled — no data received for 180s')), READ_TIMEOUT_MS)
          ),
        ]);
      } catch (timeoutErr) {
        reader.cancel().catch(() => {});
        throw timeoutErr;
      }
      const { done, value } = readResult;
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop() || '';

      for (const part of parts) {
        const dataLine = part.split('\n').find(l => l.startsWith('data: '));
        if (!dataLine) continue;

        // 2026-08-23 (lane B-1b): same root cause as `generatePlanStream`, at a DIFFERENT
        // failure rate. The `try` used to wrap this whole body and its catch re-threw only when
        // the caught message contained the lowercase substring 'failed'. That is discrimination
        // by VOCABULARY: a backend refusal worded "That section is not part of the current
        // plan.", "rate limit exceeded", "timeout" or "model overloaded" contains no 'failed',
        // so the typed error was swallowed, the loop ran to `done`, and the caller received the
        // generic `new Error('No result received from regeneration')` instead — losing the
        // backend's machine code and, with it, every reason-keyed branch downstream
        // (`regenDispatchReason`, `regenFailureMessage`, the infrastructure_halt CTA).
        //
        // Fix is structural, not a better predicate: the try covers `JSON.parse` and nothing
        // else, so the `BennieApiError` below propagates on its own identity. No prose is read
        // for control flow anywhere in this loop.
        let data: RegenStreamEvent;
        try {
          // T-12: Transform SSE event data
          data = transformKeys<RegenStreamEvent>(JSON.parse(dataLine.slice(6)));
        } catch (parseErr) {
          // Deliberate, specific case (NOT a catch-all): one frame did not parse. Skip it so a
          // garbled keepalive cannot kill a live regen; `result` is still only ever set by a
          // `complete` frame that DID parse, so no false success can come of it. Logged.
          console.warn('[requestSectionRevision] discarding unparseable SSE frame:', parseErr, dataLine);
          continue;
        }

        // Batch 02 NEW-008: stale-event discard.
        // Backend stamps every payload with a monotonic `generation` integer for this regen connection.
        // Drop any event whose generation is lower than what we've already seen (stale from a prior
        // dropped/superseded connection). Generation 0 means "not stamped" — treat as always-current.
        const eventGeneration = typeof data?.generation === 'number' ? data.generation : 0;
        if (eventGeneration > 0 && eventGeneration < lastKnownGeneration) {
          continue;
        }
        if (eventGeneration > lastKnownGeneration) {
          lastKnownGeneration = eventGeneration;
        }

        // Batch 02 NEW-006: heartbeat events are pure keepalive — they carry no progress data and must
        // not trigger UI updates. Backend emits these every 30s during the long content-generator call
        // so Azure LB doesn't drop the socket; FE silently filters them out here.
        if (data.type === 'heartbeat') continue;

        if (data.type === 'progress') {
          // WI-20 (ADR-043): resolve BE message_code to localized prose.
          const localized = resolveMessageCode(
            {
              message_code: data.messageCode,
              message_args: data.messageArgs,
              message: data.message,
            },
            'progress',
          );
          onProgress?.(data.phase ?? '', localized);
        } else if (data.type === 'complete') {
          result = data;
        } else if (data.type === 'error') {
          // CR16 F-014 (red-team pass 1 fix H-2): emit BennieApiError so mid-stream
          // errors carry the same structured body shape as the pre-stream 4xx path,
          // so the halt-clear CTA and the reason-keyed toast both stay reachable.
          //
          // 🔴 2026-08-23 (lane B-1b) — THE SLOTS WERE SWAPPED, which is why that never
          // actually worked. The mid-stream frame is
          //   { type:"error", success:false, section_id, error:<PROSE>, error_code:<CODE> }
          // (`SectionEndpoints.cs`, the `catch (SectionRegenException srex)` frame —
          // `error = srex.UserMessage, error_code = srex.ErrorCode` — and the
          // `regen_stream_interrupted` frame beside it). The pre-stream 4xx body, by
          // contrast, puts the CODE in `error` (`{ error = "infrastructure_halt", … }`).
          // This branch copied the mid-stream `error` straight into the `error` slot, so
          // every downstream reader that treats `body.error` as a code — `regenDispatchReason`,
          // `regenFailureMessage`, and the `backendError === 'infrastructure_halt'` test in
          // `WorkspaceCards` — was comparing a code against an English sentence. The CTA was
          // structurally unreachable from this path and the toast always fell to
          // `errors:fallback.retry`.
          //
          // So: the CODE goes in `error`, matching the pre-stream shape the consumers were
          // written against. The backend's prose is carried as `detail` for diagnostics only —
          // never as the discriminator, and never rendered (`regenFailureMessage` resolves
          // `errors:<code>` and falls back to catalogue copy, per frontend-rules "Error
          // Display"). When the frame carries no `error_code`, `error` stays undefined and the
          // caller gets honest generic copy: prose is NOT promoted into the code slot, because
          // that promotion IS the defect.
          const errorBody: Record<string, unknown> = {
            error: data.errorCode,
            category: data.category,
            detail: data.error,
          };
          throw new BennieApiError(
            // Diagnostic only, and deliberately NOT code-shaped — every user-facing surface on
            // this path is reason-keyed off `body.error`, and a snake_case string here would
            // read like a taxonomy member the backend does not define.
            data.errorCode
              ? `regenerate-stream error frame (${data.errorCode})`
              : 'regenerate-stream error frame with no error_code',
            500,
            errorBody,
            `regenerate-stream/${sectionId}`,
          );
        }
      }
    }

    if (!result) throw new Error('No result received from regeneration');

    // A `complete` regen event always carries version/content/status/createdAt;
    // the cast finalizes the SectionVersion shape from the optional wire fields.
    return {
      version: result.version,
      content: result.content,
      status: result.status as SectionVersion['status'],
      createdAt: result.createdAt,
      citations: result.citations,
    } as SectionVersion;
  }

  async getCitationContext(
    workspaceId: string,
    planId: string,
    sectionId: string,
    citationId: string,
    versionId?: string,
    /** F-CITE-ORPHAN-3 (2026-05-21) — diagnostic enrichment. Optional; the BE
     *  logs them on the 404-orphan path so the next post-fix RCA can isolate
     *  any surviving orphan to its source finding + originating field. */
    findingId?: string,
    fromField?: 'evidence_ref' | 'observation' | 'expected_standard' | 'impact' | 'recommendation' | 'root_cause_description',
  ): Promise<{
    success: boolean;
    citationId: string;
    context: string;
    preview: string;
    // Client-CK W3 (WI-18): sourceClass ('client' | 'bennie') is present ONLY on dual-source
    // deployments — the BE omits it otherwise (home responses unchanged).
    // 2026-09-14 (client feedback / Regan Sun; verifier finding on PR #2428): localPath is the
    // persisted full display path (SectionModels.cs CitationSource.LocalPath) — additive,
    // display-only, absent when never stamped.
    source: { filename: string; page: number; chunkId?: string; sourceClass?: 'client' | 'bennie'; localPath?: string } | null;
    type: string;
    fileType?: string;
    contentDescription?: string;
    keyTopics?: string[];
    // W4-09: Structured data fields for table/image citation rendering
    contentType?: 'text' | 'image' | 'table';
    tableData?: { headers: string[]; rows: string[][] };
    // TD-438: the persisted citation's image REFERENCE (`blob://…` or `ckfigure://…`) — the
    // resolver decides which plane serves which scheme. Both this and `contentType: 'image'`
    // were declared here long before the BE returned either, so the preview's image branch
    // could never fire from the API path; the endpoint now surfaces them off the stored citation.
    imageUrl?: string;
    ckFigureUrls?: CkFigureUrlMap;
  }> {
    const params = new URLSearchParams({ citation_id: citationId });
    if (versionId) params.set('version_id', versionId);
    if (findingId) params.set('finding_id', findingId);
    if (fromField) params.set('from_field', fromField);
    const data = await this.fetch<{
      success: boolean;
      citationId: string;
      context: string;
      preview: string;
      source: { filename: string; page: number; chunkId?: string; sourceClass?: 'client' | 'bennie'; localPath?: string } | null;
      type: string;
      fileType?: string;
      contentDescription?: string;
      keyTopics?: string[];
      contentType?: 'text' | 'image' | 'table';
      tableData?: { headers: string[]; rows: string[][] };
      imageUrl?: string;
      ckFigures?: CkFigureUrlPair[];
    }>(`/workspaces/${workspaceId}/plan/${planId}/sections/${sectionId}/citation-context?${params.toString()}`);

    return { ...data, ckFigureUrls: toCkFigureUrlMap(data.ckFigures) };
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Export
  // ─────────────────────────────────────────────────────────────────────────────

  async exportReport(request: ExportRequest): Promise<ExportResult> {
    const raw = await this.fetch<Record<string, unknown>>(`/workspaces/${request.workspaceId}/export`, {
      method: 'POST',
      body: JSON.stringify({
        // Convert camelCase domain fields back to snake_case for API
        section_versions: Array.isArray(request.sectionVersions)
          ? request.sectionVersions.map((sv) => ({ section_id: sv.sectionId, section_version_id: sv.sectionVersionId }))
          : request.sectionVersions,
        ...(request.reportVersionId ? { report_version_id: request.reportVersionId } : {}),
      }),
    }, EXPORT_TIMEOUT_MS);
    // T-12: transformer converts download_url→downloadUrl automatically
    const downloadUrl = raw.downloadUrl as string;
    if (!downloadUrl) {
      throw new Error(raw.error ? `Export failed: ${raw.error}` : 'Export succeeded but no download URL returned');
    }
    // EXPORT-1 (CR-3): Pass through data gap sections from response for FE notification
    const dataGapSections = Array.isArray(raw.dataGapSections) ? raw.dataGapSections as string[] : undefined;
    // Cleanup-time persistent warning. Wave 1 (2026-04-29) — no longer mutates at export
    // time; it reflects the cleanup outcome only and is unchanged across /export calls.
    // 🔴 2026-09-25 review round 3 — `templateStylePreserved` FULL-DELETED here and from
    // `ExportResult`: captured but never rendered by anything (the persistent banner reads
    // `templateStyleStatus` off the plan-fetch response, not this one).
    const templateStyleWarning = (raw.templateStyleWarning ?? null) as string | null;
    // Wave 1 + Wave 2.C (2026-04-29) — per-export render-path indicator and the
    // one-shot warning that fires when Path A failed and Path B rescued THIS file.
    // Surfaced to the user via Snackbar in WorkspacePage so they know the file
    // they just downloaded is generic-styled and that retrying may produce the
    // template-styled version (Path A is non-sticky on transient failure).
    const templateStyleUsedThisExport = (raw.templateStyleUsedThisExport ?? null) as ('A' | 'B' | null);
    const templateStyleWarningThisExport = (raw.templateStyleWarningThisExport ?? null) as string | null;
    const sizeBytes = typeof raw.sizeBytes === 'number' ? raw.sizeBytes : undefined;
    // ➕ B14 W-12 — the handle the MDR promotion carries, and the model it would land on.
    // Both are read defensively because a pre-W-12 API sends neither: `undefined` means
    // "this API cannot tell me", and the caller renders NO promote control rather than a
    // disabled one. ⛔ `exportId` is NOT parsed out of `downloadUrl` — see ExportResult.
    const exportId = typeof raw.exportId === 'string' ? raw.exportId : undefined;
    // ⚠️ `null` is PRESERVED and is NOT collapsed to `undefined`. They mean different
    // things: null is "this API answered, and the workspace genuinely has no parent model"
    // (ruling R9's permanent legacy set), while undefined is "this API did not answer".
    // Both suppress the control, but only one of them is a fact about the workspace.
    const modelId = typeof raw.modelId === 'string' ? raw.modelId
      : raw.modelId === null ? null
      : undefined;
    return {
      downloadUrl,
      exportId,
      modelId,
      dataGapSections,
      templateStyleWarning,
      templateStyleUsedThisExport,
      templateStyleWarningThisExport,
      sizeBytes,
    };
  }

  /**
   * F-06: fetch the rendered report bytes.
   *
   * Previously this hop ran as a bare `fetch(downloadUrl)` in WorkspacePage with no
   * timeout and — more seriously — no `response.ok` check: a 4xx/5xx body was passed
   * straight to `.blob()`, saved as `report.docx` and announced with a success toast.
   * That is the "Never show success when the backend returned an error" breach in
   * frontend-rules. Both halves of the export flow now share one bounded, checked
   * transport so a hang ends in a TimeoutError and a failure ends in a real error.
   */
  async downloadExportFile(downloadUrl: string): Promise<Blob> {
    const url = downloadUrl.startsWith('http') ? downloadUrl : `${this.baseUrl}${downloadUrl}`;
    const response = await this.withTimeout(EXPORT_TIMEOUT_MS, undefined, (signal) =>
      fetch(url, { signal })
    );
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ detail: response.statusText }));
      const message = localizeErrorBody(errorBody, response.status);
      throw new BennieApiError(message, response.status, errorBody, '/export/download');
    }
    return response.blob();
  }

  // C3-084: POST /api/workspaces/{id}/complete — explicit "Mark workspace complete"
  // lifecycle advance. Download (exportReport) is now a passive read; this is the
  // SOLE valid trigger for the terminal `complete` transition. Idempotent —
  // server returns 200 with alreadyComplete=true when the workspace is already
  // complete. Spec: the-truth.md §3.4 + §6.1a/b/c (CONSENT 2026-05-13).
  async markComplete(workspaceId: string): Promise<{
    workspaceId: string;
    state: string;
    alreadyComplete?: boolean;
  }> {
    return this.fetch<{ workspaceId: string; state: string; alreadyComplete?: boolean }>(
      `/workspaces/${workspaceId}/complete`,
      { method: 'POST' },
    );
  }

  // CR16 F-005: POST /api/workspaces/{id}/plan/{planId}/retry-generation.
  // Clears active_operations.section_gen.infrastructure_error so subsequent
  // /regenerate POSTs no longer 409. Returns { status, category, message }.
  async retryGeneration(
    workspaceId: string,
    planId: string,
  ): Promise<{ status: string; category?: string; message?: string }> {
    return this.fetch<{ status: string; category?: string; message?: string }>(
      `/workspaces/${workspaceId}/plan/${planId}/retry-generation`,
      { method: 'POST' },
    );
  }

  async getIssueLog(
    workspaceId: string, options?: { signal?: AbortSignal; runId?: string; reportVersionId?: string },
  ): Promise<IssueLogResponse> {
    // ⛔ ABSENT ⇒ NO QUERY STRING. Not `?runId=`, not `?runId=undefined` — the server treats a
    // blank value as absent, but the byte-identical-request property is what makes this widening
    // provably inert for every pre-existing caller, and an always-appended `?` would give that up.
    // F-audit-r11b-01 — `reportVersionId` rides the same rule: sent only when a version is named.
    const qs = issueLogQuery(options?.runId, options?.reportVersionId);
    return this.fetch<IssueLogResponse>(`/workspaces/${workspaceId}/issue-log${qs}`, { signal: options?.signal });
  }

  async getExportIssueOrdinals(
    workspaceId: string, options?: { signal?: AbortSignal },
  ): Promise<ExportIssueOrdinalsResponse> {
    return this.fetch<ExportIssueOrdinalsResponse>(`/workspaces/${workspaceId}/export/issue-ordinals`, { signal: options?.signal });
  }

  // Round-7 D5-i — phase 2's two recovery moves. Both fail LOUD: a 409 propagates as a
  // BennieApiError and the panel surfaces it, because a recovery action that silently did nothing
  // is exactly the class of defect this round exists to remove.
  /**
   * N2 (2026-08-19) — POST a route whose response is SSE-wrapped for front-door liveness, and hand
   * back the same value the un-wrapped route used to resolve to. Public method shapes are unchanged,
   * so `WorkspaceIssueLogPanel` needs no edit: it still awaits a promise that resolves on success and
   * rejects with a `BennieApiError` on failure.
   *
   * WHY THESE ROUTES STREAM AT ALL. Every request from this app crosses the Azure Static Web Apps
   * linked-backend proxy, which abandons a request that has produced no response bytes for long
   * enough and replaces it with its own plain-text body, `Backend call failure`. Measured bracket
   * `> 69.9 s, <= 183.7 s`; it is an IDLE wall, not a total-duration one, and it has no configuration
   * surface in any environment. `skip-aggregation` was measured at 69,071 ms on DEV (2026-08-08) and
   * both routes await an LLM in-band, so a completed recovery could be reported to the user as a
   * failure. See `wiki/concepts/front-door-request-wall.md`.
   *
   * THE WATCHDOG IS PER-READ, NOT PER-REQUEST, AND THAT IS THE POINT. A total-duration timeout here
   * would re-create the very failure the stream exists to remove — cutting a healthy long call. The
   * server emits a heartbeat every 20 s, so an idle window is the correct thing to bound. 90 s
   * tolerates four consecutive missed beats and is the same 3x-cadence ratio the datastore
   * heartbeat-liveness gate uses (30 s beat / 90 s freshness). Not a guessed wall-clock budget:
   * `resume-validation` has no config key that bounds it end to end, and inventing one here would be
   * a number with nothing behind it.
   *
   * A STREAM THAT ENDS WITHOUT A TERMINAL FRAME REJECTS. It is never treated as success — the whole
   * defect class this replaces is an incomplete operation that reads as a finished one.
   */
  private async postSseWrapped<T>(endpoint: string): Promise<T> {
    const IDLE_TIMEOUT_MS = 90_000;
    const url = `${this.baseUrl}/api${endpoint}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    // The instant guards (terminal-workspace 409, not-applicable 409, workspace 404) are still real
    // HTTP statuses with JSON bodies — the server only switches to SSE once it starts the long work.
    // Handled byte-identically to `this.fetch` so the fast-fail contract is unchanged.
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ detail: response.statusText }));
      throw new BennieApiError(localizeErrorBody(errorBody, response.status), response.status, errorBody, endpoint);
    }
    if (!response.body) throw new Error(`No response stream from ${endpoint}`);

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let terminal: { status: number; body: unknown } | null = null;
    let failure: Record<string, unknown> | null = null;

    // A transport error is NOT caught here. Unlike the chat path, these routes have no
    // evidence-gated recovery to fall back on, so a severed stream must propagate as a failure
    // rather than be converted into a missing terminal frame and re-described.
    {
      for (;;) {
        let watchdog: ReturnType<typeof setTimeout> | undefined;
        // The watchdog is cleared once the race settles — an uncleared timer per read leaks its
        // closure for the life of the call, which on a multi-minute stream is dozens of them.
        const idle = new Promise<{ done: true; value: undefined }>((resolve) => {
          watchdog = setTimeout(() => resolve({ done: true, value: undefined }), IDLE_TIMEOUT_MS);
        });
        let done: boolean;
        let value: Uint8Array | undefined;
        try {
          ({ done, value } = await Promise.race([reader.read(), idle]));
        } finally {
          if (watchdog !== undefined) clearTimeout(watchdog);
        }
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let event: Record<string, unknown>;
          try {
            // Deliberately NOT transformKeys'd here: the nested `body` must stay in its wire shape
            // so the failure branch can hand `localizeErrorBody` exactly what the un-wrapped route
            // used to hand it. Only the SUCCESS body is transformed, which is what `this.fetch` did.
            event = JSON.parse(line.slice(6)) as Record<string, unknown>;
          } catch {
            continue; // a malformed line is not a terminal state; keep reading
          }
          if (event.phase === 'heartbeat' || event.phase === 'accepted') continue;
          if (event.phase === 'complete') {
            terminal = { status: typeof event.status === 'number' ? event.status : 200, body: event.body };
          } else if (event.phase === 'error') {
            failure = event;
          }
        }
      }
    }

    if (failure) {
      const status = typeof failure.status === 'number' ? failure.status : 500;
      throw new BennieApiError(localizeErrorBody(failure, status), status, failure, endpoint);
    }
    if (!terminal) {
      // Cut mid-stream, or idle past the watchdog. The server may well have completed and persisted —
      // persist-then-render means a refetch will show it — but this call cannot claim it did.
      throw new BennieApiError(
        `The connection to ${endpoint} ended before the operation reported a result.`,
        504,
        { error: 'stream_incomplete' },
        endpoint,
      );
    }
    if (terminal.status >= 400) {
      const body = (terminal.body ?? { detail: `HTTP ${terminal.status}` }) as Record<string, unknown>;
      throw new BennieApiError(localizeErrorBody(body, terminal.status), terminal.status, body, endpoint);
    }
    return transformKeys<T>(terminal.body);
  }

  async resumeValidation(workspaceId: string, planId: string): Promise<unknown> {
    return this.postSseWrapped<unknown>(`/workspaces/${workspaceId}/plan/${planId}/resume-validation`);
  }

  async skipAggregation(workspaceId: string, planId: string): Promise<unknown> {
    return this.postSseWrapped<unknown>(`/workspaces/${workspaceId}/plan/${planId}/skip-aggregation`);
  }

  // ADR-067: read the persisted, source-numbered reference list off the workspace-scoped
  // docstore artifact (persist-then-render — never the LLM/section body; the list is not a
  // section). The endpoint 404s until the list is built at finalization (and for pre-feature
  // / pre-finalize workspaces); that is a legitimate "no reference list yet" empty state,
  // mapped to null so the drawer stays hidden rather than surfacing a user-facing error.
  // Any non-404 failure propagates loudly per fail-loud.
  async getReferenceList(
    workspaceId: string,
    planId: string,
    options?: { signal?: AbortSignal; reportVersionId?: string; runId?: string },
  ): Promise<ReferenceListResponse | null> {
    // Rev 9 §7 (PR 9): an audit run's OWN list. Same mechanism and body shape; a bare 404 is "not
    // persisted", but a TYPED 404 (`run_record_not_found` — a missing or foreign run) is a failure
    // and propagates, never "no list".
    if (options?.runId) {
      try {
        return await this.fetch<ReferenceListResponse>(
          `/workspaces/${workspaceId}/audit/runs/${encodeURIComponent(options.runId)}/reference-list`,
          { signal: options.signal },
          WORKSPACE_CRUD_TIMEOUT_MS,
        );
      } catch (err) {
        if (err instanceof BennieApiError && err.status === 404 && typeof err.body?.error !== 'string') return null;
        throw err;
      }
    }
    // Absent ⇒ no query string: the server resolves the CURRENT version, unchanged. A named version
    // reads that version's own persisted list (a superseded version stays readable).
    const qs = options?.reportVersionId ? `?reportVersionId=${encodeURIComponent(options.reportVersionId)}` : '';
    try {
      return await this.fetch<ReferenceListResponse>(
        `/workspaces/${workspaceId}/plan/${planId}/reference-list${qs}`,
        { signal: options?.signal },
      );
    } catch (err) {
      if (err instanceof BennieApiError && err.status === 404) return null;
      throw err;
    }
  }

  async getSymptom(workspaceId: string, symptomId: string, options?: { signal?: AbortSignal }): Promise<SymptomDto> {
    return this.fetch<SymptomDto>(`/workspaces/${workspaceId}/symptoms/${symptomId}`, { signal: options?.signal });
  }

  /**
   * The issue-log CSV export (validation and development). Bounded on the same ceiling as the report
   * export: the WorkspaceIssueLogPanel button disables itself on `csvDownloading` and re-enables in a
   * `finally`, so an unbounded fetch here left the same "spins forever, no way out but a reload" hole
   * the toolbox download had (frontend-rules — "loading/progress indicators must have timeout
   * fallbacks").
   */
  async exportIssueLogCsv(workspaceId: string): Promise<Blob> {
    return this.downloadExport(`/workspaces/${workspaceId}/issue-log/export.csv`, 'Issue log CSV export');
  }

  async exportAuditRunWorkbook(workspaceId: string, runId: string): Promise<Blob> {
    return this.downloadExport(
      `/workspaces/${workspaceId}/audit/runs/${encodeURIComponent(runId)}/export.xlsx?presentationLanguage=${encodeURIComponent(currentUiLanguage())}`, 'Audit workbook export');
  }

  async exportReportVersionWorkbook(workspaceId: string, reportVersionId: string): Promise<Blob> {
    return this.downloadExport(
      `/workspaces/${workspaceId}/report-versions/${encodeURIComponent(reportVersionId)}/export.xlsx?presentationLanguage=${encodeURIComponent(currentUiLanguage())}`, 'Audit workbook export');
  }

  /**
   * A bounded file download. A structured JSON error body (a typed 4xx/5xx) becomes a
   * `BennieApiError` carrying `body.error`, so callers localize the code; anything else keeps its
   * status and text. Never resolves with an error body as if it were the file.
   */
  private async downloadExport(endpoint: string, what: string): Promise<Blob> {
    const resp = await this.withTimeout(EXPORT_TIMEOUT_MS, undefined, (signal) =>
      fetch(`${this.baseUrl}/api${endpoint}`, { signal })
    );
    if (!resp.ok) {
      const ct = resp.headers.get('Content-Type') || '';
      if (ct.includes('application/json')) {
        const errorBody = await resp.json().catch(() => ({ detail: resp.statusText }));
        const message = localizeErrorBody(errorBody, resp.status);
        throw new BennieApiError(message, resp.status, errorBody, endpoint);
      }
      const text = await resp.text().catch(() => '');
      throw new BennieApiError(
        `${what} failed (HTTP ${resp.status})${text ? `: ${text}` : ''}`,
        resp.status,
        { detail: text },
        endpoint,
      );
    }
    return resp.blob();
  }


  // ─────────────────────────────────────────────────────────────────────────────
  // Chat Data Query
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Ask the backend to stop ONE named concierge turn (the ordinal from the stream's `accepted` event).
   * 202 -> `stop_requested` (cancelled on this replica) or `stop_requested_remote` (stamped for the replica
   * hosting the turn): the request was accepted; the turn's outcome is whatever it persists (a
   * stopped row, or its answer if it had already passed the point a Stop can end it), so callers read
   * persisted state rather than assume the stop took effect. 409 maps to a typed answer:
   * `no_active_turn` (nothing live yet or any more), `turn_not_current` (another turn is running),
   * `not_stoppable` (the turn is past cancellable work and finishes normally). Anything else throws.
   */
  async stopChatTurn(workspaceId: string, turnOrdinal: string): Promise<ChatStopResult> {
    try {
      const accepted = await this.fetch<{ status?: string }>(
        `/workspaces/${workspaceId}/chat/turns/${encodeURIComponent(turnOrdinal)}/stop`, { method: 'POST' });
      // `stop_requested_remote`: the turn runs on another replica, which may already have committed it;
      // only the persisted outcome says whether it stopped.
      return accepted?.status === 'stop_requested_remote' ? 'stop_requested_remote' : 'stop_requested';
    } catch (e) {
      if (e instanceof BennieApiError && e.status === 409) {
        switch (e.body?.error) {
          case 'no_active_turn': return 'no_active_turn';
          case 'turn_not_current': return 'turn_not_current';
          case 'not_stoppable_in_this_phase': return 'not_stoppable';
          default: throw e;
        }
      }
      throw e;
    }
  }

  async queryWorkspaceData(
    workspaceId: string,
    query: string,
    // TD-358 / TD-532: pre-send transcript baseline, used ONLY to prove that a message
    // found during stream-drop recovery is genuinely OURS and not an identical earlier
    // turn. REQUIRED and positioned before the optional `planId` so the compiler — not a
    // comment — is what stops a caller from omitting it. See ChatRecoveryBaseline.
    recoveryBaseline: ChatRecoveryBaseline,
    planId?: string,
    observer?: ChatStreamObserver,
    chatPlan?: ChatPlanTarget,
  ): Promise<{
    answer: string;
    metadata: Record<string, unknown>;
    intent?: string;
    plan?: { planId: string; version: number; status: string };
    sectionRegenerated?: {
    sectionId: string;
    status: string;
    content?: string;
    version?: number;
    /**
     * ⚖️ R-B4 (2026-09-05) — the PER-MEMBER outcome of a multi-section chat fan-out.
     *
     * One `section_feedback` turn can now name several sections (`target_section_ids`), and the
     * handler dispatches them through ONE staged pipeline run. Absent on a single-target turn, which
     * keeps the flat fields above and is the shape every existing client already reads.
     *
     * 🔴 `content` and `version` became OPTIONAL in the same change, and the type was previously
     * LYING about them: the exec-summary branch has always answered `{ section_id, status }` with
     * neither, because at that moment no new row exists to claim. The fan-out answers the same way,
     * for the same honest reason. Declaring them required did not make them present — it only meant
     * a reader could not be warned.
     *
     * ⚠️ It carries NO body. The FE renders from a REFETCH of persisted state
     * (frontend-rules.md persist-then-render); this list's only job is to say WHICH sections owe one.
     */
    sections?: Array<{ sectionId: string; status: string }>;
  };
  }> {
    // SSE-streamed under the hood. Mirrors generatePlanStream's reader pattern.
    // Switched from POST /chat/query to POST /chat/query-stream so response bytes
    // flow continuously and SWA's linked-backend proxy never sees an idle request
    // (heartbeat every 20s on the BE side keeps the connection open while the
    // upstream concierge call runs to completion). Public method shape unchanged so
    // callers (WorkspacePage onQueryWorkspaceData, onSendFeedback fallback) need no edits.
    //
    // ⚠️ THE WALL IS AN *IDLE* WALL AND IT IS NOT ~45s. Corrected 2026-08-19 (N2): the "~45s"
    // that stood here is Microsoft's DOCUMENTED figure for SWA API backends and is refuted as
    // the ENFORCED one — a 69,877 ms /chat/query with zero response bytes went through this
    // same proxy, and a 183,743 ms one was cut while the backend logged status=200. Measured
    // bracket: > 69.9 s, <= 183.7 s. Because it is idle-based, streamed responses are
    // effectively unbounded by it (242,090 ms and 267,648 ms on this very route, 600,228 ms on
    // /turns/stream). It has NO configuration surface in any env, so the heartbeat is the whole
    // defence. /chat/query is now internal-loopback-only and answers any other caller with a
    // typed 403 naming this route. See wiki/concepts/front-door-request-wall.md — cite that
    // bracket or re-measure; never restate a remembered number here.
    const url = `${this.baseUrl}/api/workspaces/${workspaceId}/chat/query-stream`;
    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        ...(observer?.signal ? { signal: observer.signal } : {}),
        // Auditing rework PR 10: the tab's plan kind (a hint the server checks against the persisted row)
        // and the version on screen — the optimistic-concurrency base a checklist revision requires.
        body: JSON.stringify({
          query, plan_id: planId, max_results: 10,
          ...(chatPlan ? { plan_kind: chatPlan.planKind, plan_version: chatPlan.planVersion } : {}),
        }),
        });
    } catch (e) {
      if (observer?.signal?.aborted) throw new ChatTurnAbortedError();
      throw e;
    }

    if (!response.ok) {
      // C3-113 (2026-05-18): if the BE responded with a typed ChatErrorEnvelope
      // (HTTP-status payload — distinct from the SSE error event below) the body
      // already carries error_class / retry_eligible / status_code. transformKeys
      // converts those to camelCase before we read them — preserve the envelope
      // as-is on BennieApiError.body so callers can switch on body.errorClass.
      const rawBody = await response.json().catch(() => ({ detail: response.statusText }));
      const errorBody = transformKeys<Record<string, unknown>>(rawBody);
      const message =
        (errorBody.message as string | undefined)
        || (errorBody.detail as string | undefined)
        || (errorBody.error as string | undefined)
        || `HTTP ${response.status}`;
      throw new BennieApiError(message, response.status, errorBody, `/workspaces/${workspaceId}/chat/query-stream`);
    }

    const startedAt = Date.now();
    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalEvent: Record<string, unknown> | null = null;
    let errorEvent: Record<string, unknown> | null = null;
    let transportError: unknown = null;
    // TD-532: the turn's budget as the BE itself declared it on the `accepted` event
    // (`Agents:Concierge:TimeoutSeconds`). Stays null until the BE says so — we never
    // guess a budget, we only ever repeat one we were told.
    let declaredBudgetMs: number | null = null;
    const READ_TIMEOUT_MS = 300_000; // 5min — matches generatePlanStream

    try {
      for (;;) {
        const readPromise = reader.read();
        // TD-532: the per-read watchdog timer is CLEARED once the race settles. It used to
        // leak one live 300s timer per read — ~11 of them on the measured 206s turn — each
        // holding its closure until long after the loop had exited.
        let watchdog: ReturnType<typeof setTimeout> | undefined;
        const timeoutPromise = new Promise<{ done: true; value: undefined }>((resolve) => {
          watchdog = setTimeout(() => resolve({ done: true, value: undefined }), READ_TIMEOUT_MS);
        });
        let done: boolean;
        let value: Uint8Array | undefined;
        try {
          ({ done, value } = await Promise.race([readPromise, timeoutPromise]));
        } finally {
          if (watchdog !== undefined) clearTimeout(watchdog);
        }
        if (done) break;

        // Bytes arrived, so the connection is alive. This is the signal that tells the UI a
        // long turn is still healthy — without it a 200s turn and a dead socket look identical.
        observer?.onPhase?.('waiting');

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            // T-12: transform snake_case → camelCase keys in the streamed payload too
            const event = transformKeys<Record<string, unknown>>(JSON.parse(line.slice(6)));
            // TD-532: `accepted` carries the BE's declared budget for this turn.
            if (typeof event.budgetSeconds === 'number' && event.budgetSeconds > 0) {
              declaredBudgetMs = event.budgetSeconds * 1000;
            }
            if (event.phase === 'accepted' && typeof event.turnOrdinal === 'string') {
              observer?.onAccepted?.(event.turnOrdinal);
            }
            if (event.phase === 'heartbeat') continue;
            if (event.phase === 'complete') finalEvent = event;
            else if (event.phase === 'error') errorEvent = event;
          } catch {
            // ignore malformed event lines — heartbeats keep the stream alive
          }
        }
      }
    } catch (readErr) {
      // TD-358: the socket was cut mid-stream (ingress drain, revision swap,
      // network blip). The BE turn may have completed and persisted regardless —
      // that is decided below by evidence, not assumed here.
      transportError = readErr;
    }

    // A stream we abandoned on purpose is neither a drop to recover from nor a failure to show.
    if (observer?.signal?.aborted) throw new ChatTurnAbortedError();

    if (errorEvent) {
      // The BE deliberately emitted a typed failure. This is a REAL error with a
      // known class — never recovered from (TD-358 recovery is transport-only).
      const status = typeof errorEvent.statusCode === 'number' ? errorEvent.statusCode : 500;
      const msg = (errorEvent.message as string) || `HTTP ${status}`;
      throw new BennieApiError(msg, status, errorEvent, `/workspaces/${workspaceId}/chat/query-stream`);
    }
    if (!finalEvent) {
      // TD-358 (F-RUN-02, PROD 2026-07-21) → TD-532 (PROD 2026-08-03): the stream ended —
      // cut socket or read timeout — without a `complete` event, while the turn itself may
      // be fine.
      //
      // TD-358 handled the case where the turn had ALREADY persisted when the stream died
      // (a revision-swap drain landing on a finished turn) and probed the transcript once.
      // TD-532 is the other half, measured on PROD build main-2d093d860: the client leg was
      // severed while the turn was STILL RUNNING. The backend's own socket to the
      // intermediary stayed open, so it never noticed, ran to completion, logged
      // `200 / 206,269 ms / AGENT_COMPLETE rounds=3 response_len=7338`, and persisted both
      // messages — the answer was there on reload. The single-shot probe had fired at the
      // moment of the cut, tens of seconds BEFORE any of that, found nothing, and returned
      // null; the user got "An unexpected error occurred" for a turn that fully succeeded.
      //
      // So the probe now POLLS for as long as the turn could still plausibly be running,
      // sized from the budget the BE declared on its `accepted` event. The evidence rule
      // per attempt is unchanged and still strict — a genuinely failed turn still fails
      // loudly, it just fails a bit later.
      const originalError = transportError instanceof Error
        ? transportError
        : new BennieApiError('Stream ended without complete event', 500, {}, `/workspaces/${workspaceId}/chat/query-stream`);

      observer?.onPhase?.('recovering');
      const recovered = await this.recoverPersistedChatTurn(
        workspaceId, query, recoveryBaseline, startedAt, declaredBudgetMs, READ_TIMEOUT_MS,
      );
      if (!recovered) throw originalError;
      console.warn(
        `[CHAT-RECOVERY] TD-358/TD-532: chat stream dropped for workspace ${workspaceId} but the turn persisted — rendering the persisted reply instead of an error.`
      );
      return recovered;
    }

    // WI-07 (ADR-076 W2): the `sources` parse/return leg is FULL-DELETED — the BE
    // ChatQueryResponse never emitted it (the FE always materialized []); chat grounding
    // now surfaces as persisted per-message `citations` (persist-then-render).
    const f = finalEvent as {
      answer?: string;
      metadata?: Record<string, unknown>;
      actionTaken?: string;
      intent?: string;
      plan?: { planId: string; version: number; status: string };
      sectionRegenerated?: {
    sectionId: string;
    status: string;
    content?: string;
    version?: number;
    /**
     * ⚖️ R-B4 (2026-09-05) — the PER-MEMBER outcome of a multi-section chat fan-out.
     *
     * One `section_feedback` turn can now name several sections (`target_section_ids`), and the
     * handler dispatches them through ONE staged pipeline run. Absent on a single-target turn, which
     * keeps the flat fields above and is the shape every existing client already reads.
     *
     * 🔴 `content` and `version` became OPTIONAL in the same change, and the type was previously
     * LYING about them: the exec-summary branch has always answered `{ section_id, status }` with
     * neither, because at that moment no new row exists to claim. The fan-out answers the same way,
     * for the same honest reason. Declaring them required did not make them present — it only meant
     * a reader could not be warned.
     *
     * ⚠️ It carries NO body. The FE renders from a REFETCH of persisted state
     * (frontend-rules.md persist-then-render); this list's only job is to say WHICH sections owe one.
     */
    sections?: Array<{ sectionId: string; status: string }>;
  };
    };
    return {
      answer: f.answer ?? '',
      metadata: f.metadata ?? {},
      intent: f.actionTaken || f.intent,
      plan: f.plan,
      sectionRegenerated: f.sectionRegenerated,
    };
  }

  /** TD-532: gap before each successive recovery attempt. Bounded backoff, then a steady
   *  5s beat for the remainder of the window — the turn can land at any point inside it. */
  private static recoveryProbeDelayMs(attempt: number): number {
    const ramp = [0, 1_000, 2_000, 3_000];
    return attempt < ramp.length ? ramp[attempt] : 5_000;
  }

  /**
   * TD-358 / TD-532 recovery probe. Returns the persisted assistant reply for `query` once
   * the transcript proves the turn completed, else null (caller then rethrows the original
   * transport error).
   *
   * Evidence rule — deliberately strict, so this can never manufacture a success:
   *  1. the pre-send BASELINE must be locatable in the refetched transcript, and
   *  2. our user message (exact `query` content, `chat_user`) must appear AFTER it, and
   *  3. a `chat_assistant` message must appear after THAT.
   * `GET /messages` returns oldest-first by the `created_at` column, so every "after"
   * here is the server's own ordering — never a client clock.
   *
   * Rule 1 is load-bearing and was ADDED after review (it was the defect in the first
   * cut of this fix). The BE writes `chat_user` + `chat_assistant` TOGETHER at the tail
   * of a successful turn (`PersistChatExchangeAsync`) — a failed turn persists NEITHER.
   * Without a baseline, a user who repeats a query verbatim ("make it shorter") and whose
   * NEW turn genuinely failed would match the PREVIOUS identical turn and have the error
   * silently suppressed — the exact fake-success class this fix exists to remove.
   *
   * TD-532 — WHY THIS POLLS. The rule above answers "did the turn land?", and TD-358 asked
   * it once, at the instant the stream died. That is the single worst moment to ask: a cut
   * that lands mid-turn is answered "no" by a transcript that simply has not been written
   * yet, and "not yet" was indistinguishable from "never". So the same question is now
   * asked repeatedly until the turn could no longer be running.
   *
   * The window is DERIVED, never picked: `budgetMs` is what the BE declared on its
   * `accepted` event, read from `Agents:Concierge:TimeoutSeconds` in
   * `apps/api/src/Bennie.Api/appsettings.json` (660s as of 2026-08-14; the concierge's
   * agent-loop budget). Raise the budget there and
   * this window follows it with nothing to remember — which is exactly what happened when
   * the chat-retrieval salvage wave took it 270 -> 660 to fit two deep retrievals in one
   * turn: this file needed no edit for the window itself, only for the claim below.
   *
   * If the stream died before the BE declared anything, we fall back to the client's OWN
   * already-declared ceiling for a turn (`READ_TIMEOUT_MS`, 300s) rather than inventing a
   * third number. That fallback is now SHORTER than the real budget, and the superseded
   * sentence here — "appsettings pins the concierge budget below the 300s loopback timeout,
   * so it is the honest outer bound" — stopped being true at that raise. It is kept anyway,
   * deliberately: the fallback only fires when the stream died before `accepted`, which is
   * flushed at t≈0, so the window in which it binds is milliseconds wide and the honest
   * reading is "the client waited its own ceiling because the server never told it one" —
   * not a guess at a budget it was never given. Raising READ_TIMEOUT_MS to match would also
   * move the per-read watchdog above, which is a different control with a different job
   * (the BE heartbeats every 20s, so a 300s gap between reads is already pathological).
   *
   * Either way the clock runs from `startedAt`, so a turn already 500s into a 660s budget
   * is only waited on for the ~160s it has left, plus a settle margin for the persist and
   * the round trip that follow the agent's last token.
   *
   * A failure of the probe itself is logged and RETRIED (the transport that just severed
   * the stream is exactly the transport this probe rides); only the final attempt's failure
   * decides. Every exit logs which of the rules fired, because the PROD occurrence this
   * fixes was undiagnosable after the fact: two of the outcomes returned null in silence.
   */
  private async recoverPersistedChatTurn(
    workspaceId: string,
    query: string,
    baseline: ChatRecoveryBaseline,
    startedAt: number,
    declaredBudgetMs: number | null,
    clientCeilingMs: number,
  ): Promise<{ answer: string; metadata: Record<string, unknown>; intent?: string } | null> {
    if (baseline.kind === 'unavailable') {
      // The TD-358 rule stands: freshness cannot be proven, so nothing is adopted. What
      // changed is that this is now a sentence the caller wrote, and it says why.
      console.warn(
        `[CHAT-RECOVERY] TD-532: no usable baseline for workspace ${workspaceId} (reason=${baseline.reason}) — refusing to recover; surfacing the stream error.`
      );
      return null;
    }

    // Settle margin: the agent's budget covers the agent loop, but persistence and the
    // response hop happen after it. 15s keeps a turn that finishes at the very edge of its
    // budget inside the window instead of one round trip outside it.
    const SETTLE_MARGIN_MS = 15_000;
    const budgetMs = declaredBudgetMs ?? clientCeilingMs;
    const deadline = startedAt + budgetMs + SETTLE_MARGIN_MS;
    const budgetSource = declaredBudgetMs !== null ? 'be-declared' : 'client-ceiling';

    let attempt = 0;
    let lastOutcome = 'none';
    for (;;) {
      const delay = HttpBennieClient.recoveryProbeDelayMs(attempt);
      if (delay > 0) await new Promise((r) => setTimeout(r, delay));
      attempt += 1;

      const outcome = await this.probePersistedChatTurnOnce(workspaceId, query, baseline);
      if (outcome.kind === 'saved-error') throw outcome.error;
      if (outcome.kind === 'found') {
        console.warn(
          `[CHAT-RECOVERY] TD-532: persisted turn located for workspace ${workspaceId} on attempt ${attempt} (${Date.now() - startedAt}ms after send).`
        );
        return outcome.reply;
      }
      lastOutcome = outcome.kind;

      // `baseline-absent` is the one outcome retrying cannot fix: the anchor is missing from
      // the transcript (purge / pagination / contract drift), which no amount of waiting
      // changes, and continuing to poll would only delay an error we already owe the user.
      if (outcome.kind === 'baseline-absent') break;
      if (Date.now() >= deadline) break;
    }

    console.warn(
      `[CHAT-RECOVERY] TD-532: gave up for workspace ${workspaceId} after ${attempt} attempt(s) over ` +
      `${Date.now() - startedAt}ms — last outcome=${lastOutcome}, budget=${budgetMs}ms (${budgetSource}). ` +
      `Surfacing the original stream error.`
    );
    return null;
  }

  /**
   * One application of the TD-358 evidence rule. Separated from the polling loop so the rule
   * is stated exactly once and each attempt reports WHICH rule stopped it, rather than
   * collapsing four different situations into a bare `null`.
   */
  private async probePersistedChatTurnOnce(
    workspaceId: string,
    query: string,
    baseline: Exclude<ChatRecoveryBaseline, { kind: 'unavailable' }>,
  ): Promise<
    | { kind: 'found'; reply: { answer: string; metadata: Record<string, unknown>; intent?: string } }
    | { kind: 'saved-error'; error: Error }
    | { kind: 'baseline-absent' }
    | { kind: 'user-message-absent' }
    | { kind: 'assistant-reply-absent' }
    | { kind: 'probe-failed' }
  > {
    try {
      const msgs = await this.getWorkspaceMessages(workspaceId);

      // (1) Anchor to the pre-send baseline. An empty transcript means every message is
      // new. A supplied id that is absent means we cannot prove freshness
      // (purge/pagination/contract drift) — refuse to recover rather than guess.
      let baselineIdx = -1;
      if (baseline.kind === 'anchored') {
        baselineIdx = msgs.findIndex((m) => m.id === baseline.lastKnownMessageId);
        if (baselineIdx === -1) {
          console.warn(`[CHAT-RECOVERY] TD-358: baseline message ${baseline.lastKnownMessageId} not found in the refetched transcript — refusing to recover.`);
          return { kind: 'baseline-absent' };
        }
      }

      // (2) Our user message, searched backward but floored at the baseline: it must be
      // one this send created, never an identical earlier turn.
      let userIdx = -1;
      for (let i = msgs.length - 1; i > baselineIdx; i--) {
        const m = msgs[i];
        if (m.type === 'chat_user' && m.content === query) { userIdx = i; break; }
      }
      if (userIdx === -1) return { kind: 'user-message-absent' };

      // (3) The assistant reply that followed it.
      for (let i = userIdx + 1; i < msgs.length; i++) {
        const m = msgs[i];
        if (m.type !== 'chat_assistant') continue;
        // `metadata` came through parseJson → transformKeys, which recurses into nested
        // objects, so the wire's `action_taken` is `actionTaken` here. The snake_case
        // fallback this used to carry could never match.
        const meta = (m.metadata ?? {}) as Record<string, unknown>;
        // TD-1204: a saved revision can settle with a typed confirmation failure. Its exact
        // envelope is persisted by the existing assistant writer; row presence is not success.
        if ('chatError' in meta) {
          const saved = meta.chatError;
          if (typeof saved !== 'object' || saved === null) {
            return { kind: 'saved-error', error: new Error('PERSISTED_CHAT_ERROR_CONTRACT_INVALID') };
          }
          const error = saved as Record<string, unknown>;
          const detail = error.detail;
          if (error.errorClass !== 'plan_revision_saved_response_failed' || error.retryEligible !== false
            || error.phase !== 'error' || error.statusCode !== 503 || typeof error.message !== 'string'
            || typeof detail !== 'object' || detail === null
            || typeof (detail as Record<string, unknown>).planId !== 'string'
            || typeof (detail as Record<string, unknown>).planVersion !== 'number') {
            return { kind: 'saved-error', error: new Error('PERSISTED_CHAT_ERROR_CONTRACT_INVALID') };
          }
          return { kind: 'saved-error', error: new BennieApiError(error.message, error.statusCode, error,
            `/workspaces/${workspaceId}/chat/query-stream`) };
        }
        const actionTaken = typeof meta.actionTaken === 'string' ? meta.actionTaken : undefined;
        return { kind: 'found', reply: { answer: m.content, metadata: meta, intent: actionTaken } };
      }
      return { kind: 'assistant-reply-absent' };
    } catch (probeErr) {
      // Outcome is already safe (the caller rethrows the ORIGINAL error if we never
      // recover), but a silent swallow would let recovery rot undetected — log so a broken
      // probe is visible rather than quietly regressing TD-358.
      console.warn('[CHAT-RECOVERY] TD-358: recovery probe attempt failed.', probeErr);
      return { kind: 'probe-failed' };
    }
  }

  // SF-055: Workspace message log
  async getWorkspaceMessages(workspaceId: string): Promise<WorkspaceMessage[]> {
    const url = `${this.baseUrl}/api/workspaces/${workspaceId}/messages`;
    // Bounded by WORKSPACE_CRUD_TIMEOUT_MS (2026-08-28) — a hydration read that used to be able to
    // hang for ever. `withTimeout` keeps this method's own status handling below unchanged.
    const response = await this.withTimeout(WORKSPACE_CRUD_TIMEOUT_MS, undefined, (signal) => fetch(url, { signal }));
    if (!response.ok) {
      throw new Error(`Failed to fetch workspace messages: ${response.status} ${response.statusText}`);
    }
    const data = await this.parseJson<{ messages?: WorkspaceMessage[] }>(response);
    return data.messages || [];
  }

  async getWorkflowState(workspaceId: string): Promise<{ state: string | null; ingestProgress?: PersistedIngestProgress; activeOperations?: ActiveOperations; completenessPhase?: string; completenessPhases?: Partial<Record<AuditPhase, string>>; etlCompletedAt?: string | null; planFailureClass?: string | null }> {
    const url = `${this.baseUrl}/api/status/${workspaceId}/${workspaceId}`;
    // Bounded by WORKSPACE_CRUD_TIMEOUT_MS (2026-08-28). This one is read by the hydration set AND
    // by the workflow-state poll, so an unbounded call could wedge either; the bound belongs to the
    // READ rather than to one of its callers, which is why it sits here and not at a call site.
    const response = await this.withTimeout(WORKSPACE_CRUD_TIMEOUT_MS, undefined, (signal) => fetch(url, { signal }));
    if (!response.ok) {
      throw new Error(`Failed to fetch workflow state: ${response.status} ${response.statusText}`);
    }
    // T-12: parseJson deep-transforms snake_case → camelCase via transformKeys().
    // All reads below MUST use the camelCase names — the snake_case keys no
    // longer exist on `data` at this point. A prior version read
    // `data.completeness_phase` and `data.etl_completed_at`, which silently
    // returned undefined/null and made the completeness hook loop on its
    // "wait for indexer" branch until the 120s safety-net fired a bogus
    // "Data ingestion timed out" toast even though ETL had completed long ago.
    const data = await this.parseJson<{
      state?: string;
      ingestProgress?: PersistedIngestProgress;
      activeOperations?: ActiveOperations;
      completenessPhase?: string;
      completenessPhases?: Record<string, unknown>;
      etlCompletedAt?: string | null;
      planFailureClass?: string | null;
    }>(response);
    return {
      // WI-11-12: an absent `state` stays ABSENT. This previously read `data.state || 'draft'`,
      // which invented a real workflow state for a response that carried none — the-truth.md §1 P6
      // ("null/empty coercion that hides API failures FROM CALLERS"). The BE now reports
      // `state: null` when the workflow_state doc is missing (StatusEndpoints), and the coercion
      // here would have re-fabricated "draft" one layer up, so the two halves only work together.
      // Callers in this tree are ALREADY written for a falsy state — `wf.state && …`,
      // `wf.state?.startsWith(…)`, `wf?.state` — so null flows through the guards that exist.
      state: data.state ?? null,
      ingestProgress: data.ingestProgress,
      activeOperations: data.activeOperations || undefined,
      // B14 A-3: completeness_phase → completenessPhase (post-transform).
      completenessPhase: data.completenessPhase || undefined,
      // Auditing rework rev 9 §10 (PR 7): per-phase completeness progress,
      // `completeness_phases[<phase>]`. Its KEYS are phase wire values, and `transformKeys` has
      // camel-cased them (`design_effective` → `designEffective`), so both spellings are read back.
      completenessPhases: readCompletenessPhases(data.completenessPhases),
      // CR20 F-20-13: etl_completed_at → etlCompletedAt (post-transform).
      // ISO timestamp once PHASE2_COMPLETE fires; null until then.
      etlCompletedAt: data.etlCompletedAt ?? null,
      // plan_failure_class → planFailureClass: the typed plan-gen failure code the BE persists
      // WITH workflow_state = plan_failed (WorkflowStateRepository.SetPlanFailedAsync). Absent
      // stays null — the tracker then shows the generic failure copy, never an empty state.
      planFailureClass: data.planFailureClass ?? null,
    };
  }

  async getPlanGenProgress(workspaceId: string): Promise<{
    phase: string;
    progress: number;
    message?: string;
    sections?: string[];
    total?: number;
    completed?: number;
    enrichedSections?: string[];
  } | null> {
    // B13 WI-71 — A READ FAILURE AND "NO MILESTONE YET" ARE DIFFERENT ANSWERS.
    //
    // This used to be `if (!response.ok) return null;` over `catch { return null; }`,
    // which made a 500, a dropped connection and an unparseable body BYTE-IDENTICAL
    // to the backend's own honest "generation has published no milestone yet"
    // (a 200 carrying `progress: null`). Every one of the four callers branches on
    // `if (milestones)`, so a failing endpoint took the same do-nothing path as a
    // healthy one and the card sat on whatever it last drew — the maskover
    // `core-principles.md` "Fail Loud, Log Structured" prohibits, and the named-
    // absence discipline `verification-discipline.md` §2 requires.
    //
    // `null` now means EXACTLY ONE THING: the server answered, and there is no
    // milestone. A failure throws. This is safe by construction, not by hope —
    // all four call sites (WorkspacePage.tsx rehydrate / pollForPlan /
    // hydrateSectionsIfNeeded / the SSE-drop recovery poller) already sit inside a
    // try/catch or a `.catch`, and each of those branches already does the right
    // thing for "I could not read it" (keep polling; show the generic in-progress
    // label) rather than the wrong thing for "there is nothing to read".
    const url = `${this.baseUrl}/api/workspaces/${workspaceId}/plan/generation-progress`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`plan generation-progress read failed: HTTP ${response.status}`);
    }
    const data = await this.parseJson<{
      progress?: {
        phase: string;
        progress: number;
        message?: string;
        sections?: string[];
        total?: number;
        completed?: number;
        enrichedSections?: string[];
      } | null;
    }>(response);
    return data.progress ?? null;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Analytics — Slice 1
  // ─────────────────────────────────────────────────────────────────────────────

  // B4-13: `until` (inclusive upper bound, ISO 8601) added as second param.
  // Either/both of since/until activate the live-query path on the BE.
  private analyticsQs(since?: string, until?: string): string {
    const parts: string[] = [];
    if (since) parts.push(`since=${encodeURIComponent(since)}`);
    if (until) parts.push(`until=${encodeURIComponent(until)}`);
    return parts.length > 0 ? `?${parts.join('&')}` : '';
  }

  async getAnalyticsSummary(
    since?: string, until?: string, options?: { signal?: AbortSignal; includeArchived?: boolean },
  ): Promise<AnalyticsSummary> {
    const qs = this.analyticsQs(since, until);
    const archived = options?.includeArchived ? `${qs ? '&' : '?'}include_archived=true` : '';
    return this.fetch<AnalyticsSummary>(`/analytics/summary${qs}${archived}`, { signal: options?.signal });
  }

  // Analytics — Parity Slice
  // ─────────────────────────────────────────────────────────────────────────────

  async getWorkspacePipeline(since?: string, until?: string, options?: { signal?: AbortSignal }): Promise<WorkspacePipelineRow[]> {
    return this.fetch<WorkspacePipelineRow[]>(`/analytics/workspace-pipeline${this.analyticsQs(since, until)}`, { signal: options?.signal });
  }

  async getAnalyticsToolboxUsage(since?: string, until?: string, options?: { signal?: AbortSignal }): Promise<AnalyticsToolboxUsage> {
    // Cycle 3 Batch 12 (analytics test-run → toolbox-usage swap): /api/analytics/test-runs
    // retired in favor of /api/analytics/toolbox-usage. Reads the `toolbox_jobs` substrate
    // (cross-partition, partition key /owner). The transformer auto-converts snake_case
    // wire fields (by_tool / total_runs_30d / active_runs / success_rate /
    // top_failure_categories / avg_duration_ms) to camelCase before returning.
    return this.fetch<AnalyticsToolboxUsage>(`/analytics/toolbox-usage${this.analyticsQs(since, until)}`, { signal: options?.signal });
  }

  // Batch 2 T-01 — canonical workflow-state taxonomy
  async getWorkflowStates(options?: { signal?: AbortSignal }): Promise<WorkflowStatesResponse> {
    return this.fetch<WorkflowStatesResponse>(`/analytics/workflow-states`, { signal: options?.signal });
  }

  // Batch 2 X-10 — Admin Recent Activity feed
  async getRecentActivity(sinceHours: number, options?: { signal?: AbortSignal }): Promise<ActivityEntry[]> {
    return this.fetch<ActivityEntry[]>(`/analytics/activity?since=${sinceHours}h`, { signal: options?.signal });
  }

  // Prep-round P-0d — canonical severity taxonomy
  async getSeverityTaxonomy(
    opts?: { workspaceId?: string; serviceType?: string; signal?: AbortSignal },
  ): Promise<SeverityTaxonomyResponse> {
    const parts: string[] = [];
    if (opts?.workspaceId) parts.push(`workspace_id=${encodeURIComponent(opts.workspaceId)}`);
    if (opts?.serviceType) parts.push(`service_type=${encodeURIComponent(opts.serviceType)}`);
    const qs = parts.length > 0 ? `?${parts.join('&')}` : '';
    return this.fetch<SeverityTaxonomyResponse>(`/taxonomy/severity${qs}`, { signal: opts?.signal });
  }

  // Cycle 4 F-RCA-FE-1 / F-RCA-CONFIG-1 — per-service-type render capabilities
  async getServiceTypeCapabilities(
    opts: { workspaceId?: string; serviceType?: string; signal?: AbortSignal },
  ): Promise<import('../BennieClient').ServiceTypeCapabilitiesResponse> {
    const parts: string[] = [];
    if (opts.workspaceId) parts.push(`workspace_id=${encodeURIComponent(opts.workspaceId)}`);
    if (opts.serviceType) parts.push(`service_type=${encodeURIComponent(opts.serviceType)}`);
    if (parts.length === 0) {
      throw new Error('getServiceTypeCapabilities: workspaceId or serviceType is required');
    }
    const qs = `?${parts.join('&')}`;
    return this.fetch<import('../BennieClient').ServiceTypeCapabilitiesResponse>(
      `/services/capabilities${qs}`,
      { signal: opts.signal },
    );
  }

  // Helpers
  // ─────────────────────────────────────────────────────────────────────────────

  normalizePlanItems(items: RawPlanItem[]): PlanItem[] {
    // A wire plan item always carries an id + title (under either the canonical
    // or the legacy alias key); the `as PlanItem` finalizes the shape from the
    // optional raw fields without altering the existing fallback semantics.
    return items.map((item): PlanItem => ({
      id: item.id || item.templateSectionId,
      title: item.title || item.sectionTitle,
      description: item.description,
      requirements: item.requirements,
      type: item.type,
      // Section purpose, decoupled from render-shape `type`. Phase 2C gate routes on this.
      // Without this field threaded, FE consumers (WorkspaceCards.isExecSummarySectionId)
      // see undefined category for every plan item and dispatch ES retries through the
      // body /regenerate path instead of /phase2c/refire.
      category: item.category,
      subItems: item.subItems ? this.normalizePlanItems(item.subItems) : undefined,
      // CR17 WI-3: method (HOW) + expected_output (WHAT) paired narratives. The wire's
      // `expected_output` arrives as `expectedOutput`; the domain PlanItem field keeps
      // its snake_case name, so this is a rename, not a passthrough. (`item.expected_output`
      // used to be read first here — dead, and only the fallback kept the field alive.)
      method: item.method,
      expected_output: item.expectedOutput,
    } as PlanItem));
  }
}
